/**
 * @fileoverview MPN (Most Probable Number) Lookup Tables and Functions
 * 
 * This module provides lookup tables and functions for calculating Most Probable Number (MPN)
 * values for different types of microbiological testing:
 * 
 * - Standard quantification (getQtMpn)
 * - 2000-well format high-throughput analysis (getQt2KMpn)  
 * - Legionella-specific testing (getQtLegio)
 * 
 * The arrays are kept as large static lookups for optimal performance and minimal memory usage.
 */

/**
 * MPN (Most Probable Number) result tuple
 * [0]: Lower bound (number or string like '<1.0')
 * [1]: Most probable number estimate
 * [2]: Upper bound (number or string like 'infinite')
 */
export type MpnResult = [string | number, number, string | number];

/**
 * MPN lookup result - either a valid MPN tuple or undefined for invalid inputs
 */
export type Mpn = MpnResult | undefined;

// Array bounds constants for validation
/** Maximum index for standard MPN lookup (0-51) */
export const MAX_QT_MPN_INDEX = 51;
/** Maximum large position for 2K MPN lookup (0-49) */
export const MAX_2K_LARGE_POS = 49;
/** Maximum small position for 2K MPN lookup (0-48) */
export const MAX_2K_SMALL_POS = 48;
/** Maximum large position for Legionella MPN lookup (0-6) */
export const MAX_LEGIO_LARGE_POS = 6;
/** Maximum small position for Legionella MPN lookup (0-90) */
export const MAX_LEGIO_SMALL_POS = 90;

// Internal calculation constants
const LEGIO_MULTIPLIER = 7;
const MPN_2K_MULTIPLIER = 49;

/**
 * Standard MPN lookup table for basic dilution series
 * Index represents the count of positive wells
 */
const mpn: MpnResult[] = [
  ['<1.0', 0, 3.7],
  [1, 0.3, 5.6],
  [2, 0.6, 7.3],
  [3.1, 1.1, 9],
  [4.2, 1.7, 10.7],
  [5.3, 2.3, 12.3],
  [6.4, 3, 13.9],
  [7.5, 3.7, 15.5],
  [8.7, 4.5, 17.1],
  [9.9, 5.3, 18.8],
  [11.1, 6.1, 20.5],
  [12.4, 7, 22.1],
  [13.7, 7.9, 23.9],
  [15, 8.8, 25.7],
  [16.4, 9.8, 27.5],
  [17.8, 10.8, 29.4],
  [19.2, 11.9, 31.3],
  [20.7, 13, 33.3],
  [22.2, 14.1, 35.2],
  [23.8, 15.3, 37.3],
  [25.4, 16.5, 39.4],
  [27.1, 17.7, 41.6],
  [28.8, 19, 43.9],
  [30.6, 20.4, 46.3],
  [32.4, 21.8, 48.7],
  [34.4, 23.3, 51.2],
  [36.4, 24.7, 53.9],
  [38.4, 26.4, 56.6],
  [40.6, 28, 59.5],
  [42.9, 29.7, 62.5],
  [45.3, 31.5, 65.6],
  [47.8, 33.4, 69],
  [50.4, 35.4, 72.5],
  [53.1, 37.5, 76.2],
  [56, 39.7, 80.1],
  [59.1, 42, 84.4],
  [62.4, 44.6, 88.8],
  [65.9, 47.2, 93.7],
  [69.7, 50, 99],
  [73.8, 53.1, 104.8],
  [78.2, 56.4, 111.2],
  [83.1, 59.9, 118.3],
  [88.5, 63.9, 126.2],
  [94.5, 68.2, 135.4],
  [101.3, 73.1, 146],
  [109.1, 78.6, 158.7],
  [118.4, 85, 174.5],
  [129.8, 92.7, 195],
  [144.5, 102.3, 224.1],
  [165.2, 115.2, 272.2],
  [200.5, 135.8, 387.6],
  ['>200.5', 146.1, 'infinite'],
];

/**
 * MPN lookup table for 2000-well format
 * Used for high-throughput analysis with large and small position indices
 */
const mpn2k: MpnResult[] = [
  ['<1', 0.0, 3.7],
  [1.0, 0.0, 3.7],
  [2.0, 0.3, 5.6],
  [3.0, 0.6, 7.3],
  [4.0, 1.1, 8.9],
  [5.0, 1.7, 10.5],
  [6.0, 2.3, 12.1],
  [7.0, 2.9, 13.7],
  [8.0, 3.7, 15.3],
  [9.0, 4.5, 15.8],
  [10.0, 5.2, 16.9],
  [11.0, 5.9, 18.5],
  [12.0, 6.9, 20.1],
  [13.0, 7.8, 21.2],
  [14.1, 8.6, 21.9],
  [15.1, 9.0, 23.4],
  [16.1, 9.6, 24.9],
  [17.1, 10.5, 25.7],
  [18.1, 11.5, 26.9],
  [19.1, 12.5, 28.6],
  [20.2, 13.2, 29.3],
  [21.2, 13.9, 30.5],
  [22.2, 14.5, 31.8],
  [23.3, 15.7, 33.1],
  [24.3, 16.4, 34.2],
  [25.3, 17.6, 35.2],
  [26.4, 18.3, 36.5],
  [27.4, 19.5, 37.7],
  [28.4, 19.7, 38.6],
  [29.5, 21.0, 39.9],
  [30.5, 21.7, 41.2],
  [31.5, 22.5, 42.3],
  [32.6, 23.9, 43.4],
  [33.6, 24.6, 44.4],
  [34.7, 25.4, 45.7],
  [35.7, 26.2, 46.8],
  [36.8, 27.7, 48.0],
  [37.8, 28.5, 49.0],
  [38.9, 29.2, 50.3],
  [40.0, 30.0, 51.2],
  [41.0, 30.8, 52.8],
  [42.1, 31.6, 53.7],
  [43.1, 33.3, 54.7],
  [44.2, 34.1, 56.1],
  [45.3, 34.9, 57.1],
  [46.3, 35.7, 58.1],
  [47.4, 37.5, 59.5],
  [48.5, 38.3, 60.7],
  [49.5, 39.2, 61.6],
  [1.0, 0.1, 5.5],
  [2.0, 0.3, 5.9],
  [3.0, 0.6, 7.3],
  [4.0, 1.1, 8.9],
  [5.0, 1.7, 10.5],
  [6.0, 2.3, 12.1],
  [7.1, 3.0, 13.7],
  [8.1, 3.7, 15.3],
  [9.1, 4.3, 16.2],
  [10.1, 5.2, 17.2],
  [11.1, 6.0, 18.5],
  [12.1, 6.8, 20.1],
  [13.2, 7.6, 21.7],
  [14.2, 8.7, 22.2],
  [15.2, 9.4, 23.6],
  [16.2, 9.7, 25.1],
  [17.3, 10.6, 26.4],
  [18.3, 11.6, 27.0],
  [19.3, 12.6, 28.6],
  [20.4, 13.3, 30.0],
  [21.4, 14.0, 30.7],
  [22.4, 14.7, 32.3],
  [23.5, 15.8, 33.1],
  [24.5, 17.0, 34.5],
  [25.6, 17.2, 35.7],
  [26.6, 18.4, 37.1],
  [27.7, 19.2, 38.1],
  [28.7, 20.5, 39.2],
  [29.8, 21.2, 40.6],
  [30.8, 22.0, 41.6],
  [31.9, 22.7, 42.7],
  [32.9, 23.5, 44.0],
  [34.0, 24.9, 44.9],
  [35.0, 25.7, 46.4],
  [36.1, 26.4, 47.2],
  [37.2, 27.9, 48.8],
  [38.2, 28.7, 49.6],
  [39.3, 29.5, 51.0],
  [40.4, 30.4, 52.0],
  [41.4, 31.2, 53.1],
  [42.5, 32.8, 54.5],
  [43.6, 33.6, 55.4],
  [44.7, 34.5, 56.6],
  [45.7, 35.3, 58.0],
  [46.8, 36.1, 58.9],
  [47.9, 37.0, 60.1],
  [49.0, 38.7, 61.3],
  [50.1, 39.6, 62.5],
  [51.2, 40.5, 63.6],
  [2.0, 0.3, 7.1],
  [3.0, 0.7, 7.4],
  [4.1, 1.2, 9.0],
  [5.1, 1.6, 10.6],
  [6.1, 2.3, 12.1],
  [7.1, 3.0, 13.7],
  [8.1, 3.7, 15.3],
  [9.2, 4.4, 16.9],
  [10.2, 5.3, 17.8],
  [11.2, 6.0, 18.6],
  [12.2, 6.8, 20.1],
  [13.3, 7.7, 21.7],
  [14.3, 8.5, 22.9],
  [15.4, 9.4, 24.0],
  [16.4, 9.8, 25.1],
  [17.4, 10.4, 26.8],
  [18.5, 11.4, 27.5],
  [19.5, 12.4, 28.8],
  [20.6, 13.5, 30.4],
  [21.6, 14.1, 31.2],
  [22.7, 14.8, 32.6],
  [23.7, 16.0, 33.8],
  [24.8, 17.2, 35.0],
  [25.8, 17.9, 36.1],
  [26.9, 18.1, 37.1],
  [27.9, 19.4, 38.5],
  [29.0, 20.7, 39.7],
  [30.0, 21.4, 40.6],
  [31.1, 22.2, 42.0],
  [32.2, 23.6, 43.4],
  [33.2, 24.4, 44.4],
  [34.3, 25.1, 45.7],
  [35.4, 25.9, 46.6],
  [36.5, 26.7, 48.0],
  [37.5, 28.2, 48.9],
  [38.6, 29.0, 50.3],
  [39.7, 29.9, 51.2],
  [40.8, 30.7, 52.8],
  [41.9, 31.5, 53.7],
  [43.0, 32.3, 54.9],
  [44.0, 34.0, 56.2],
  [45.1, 34.8, 57.1],
  [46.2, 35.7, 58.6],
  [47.3, 36.5, 59.7],
  [48.4, 37.4, 60.8],
  [49.5, 38.2, 61.8],
  [50.6, 40.0, 63.0],
  [51.7, 40.9, 64.2],
  [52.8, 41.8, 65.4],
  [3.1, 0.7, 8.9],
  [4.1, 1.2, 9.1],
  [5.1, 1.7, 10.6],
  [6.2, 2.3, 12.1],
  [7.2, 3.0, 13.7],
  [8.2, 3.6, 15.3],
  [9.2, 4.4, 16.9],
  [10.3, 5.1, 18.3],
  [11.3, 6.1, 18.8],
  [12.4, 6.9, 20.2],
  [13.4, 7.7, 21.8],
  [14.5, 8.6, 23.4],
  [15.5, 9.5, 24.1],
  [16.5, 10.2, 25.3],
  [17.6, 10.5, 26.9],
  [18.6, 11.5, 28.2],
  [19.7, 12.5, 29.2],
  [20.8, 13.6, 30.4],
  [21.8, 14.3, 31.8],
  [22.9, 15.0, 32.7],
  [23.9, 15.7, 34.2],
  [25.0, 16.8, 35.2],
  [26.1, 18.1, 36.5],
  [27.1, 18.8, 37.7],
  [28.2, 19.6, 39.1],
  [29.3, 20.9, 40.2],
  [30.4, 21.6, 41.3],
  [31.4, 22.4, 42.7],
  [32.5, 23.2, 43.6],
  [33.6, 24.6, 44.9],
  [34.7, 25.4, 46.2],
  [35.8, 26.2, 47.2],
  [36.8, 27.0, 48.6],
  [37.9, 27.8, 49.5],
  [39.0, 29.3, 51.0],
  [40.1, 30.2, 52.0],
  [41.2, 31.0, 53.2],
  [42.3, 31.8, 54.5],
  [43.4, 32.6, 55.4],
  [44.5, 34.3, 57.0],
  [45.6, 35.2, 58.0],
  [46.7, 36.0, 59.0],
  [47.8, 36.9, 60.2],
  [48.9, 37.8, 61.5],
  [50.0, 38.6, 62.6],
  [51.2, 40.5, 63.8],
  [52.3, 41.4, 65.0],
  [53.4, 42.2, 66.1],
  [54.5, 43.1, 67.3],
  [4.1, 1.7, 9.5],
  [5.2, 1.8, 10.8],
  [6.2, 2.4, 12.2],
  [7.2, 2.9, 13.7],
  [8.3, 3.6, 15.3],
  [9.3, 4.5, 16.9],
  [10.4, 5.2, 18.5],
  [11.4, 5.9, 19.5],
  [12.5, 6.9, 20.5],
  [13.5, 7.8, 21.8],
  [14.6, 8.7, 23.4],
  [15.6, 9.6, 24.6],
  [16.7, 10.3, 25.7],
  [17.8, 10.9, 26.9],
  [18.8, 11.6, 28.6],
  [19.9, 12.6, 29.3],
  [21.0, 13.7, 30.7],
  [22.0, 14.8, 32.2],
  [23.1, 15.1, 33.1],
  [24.2, 15.8, 34.5],
  [25.3, 17.0, 35.7],
  [26.3, 18.3, 37.1],
  [27.4, 19.0, 38.1],
  [28.5, 19.8, 39.2],
  [29.6, 20.5, 40.6],
  [30.7, 21.9, 41.8],
  [31.8, 22.6, 42.8],
  [32.8, 23.4, 44.2],
  [33.9, 24.9, 45.6],
  [35.0, 25.7, 46.5],
  [36.1, 26.5, 48.0],
  [37.2, 27.3, 48.8],
  [38.3, 28.1, 50.3],
  [39.4, 29.7, 51.2],
  [40.5, 30.5, 52.8],
  [41.6, 31.3, 53.7],
  [42.8, 32.2, 55.2],
  [43.9, 33.0, 56.2],
  [45.0, 34.7, 57.4],
  [46.1, 35.6, 58.6],
  [47.2, 36.4, 59.8],
  [48.3, 37.3, 60.9],
  [49.5, 38.2, 62.5],
  [50.6, 40.0, 63.5],
  [51.7, 40.9, 64.7],
  [52.9, 41.8, 66.0],
  [54.0, 42.7, 67.3],
  [55.1, 43.6, 68.4],
  [56.3, 44.5, 69.4],
  [5.2, 2.3, 11.9],
  [6.3, 2.5, 12.7],
  [7.3, 2.9, 13.9],
  [8.4, 3.7, 15.3],
  [9.4, 4.3, 16.9],
  [10.5, 5.2, 18.5],
  [11.5, 6.0, 20.1],
  [12.6, 6.8, 21.2],
  [13.7, 7.6, 21.9],
  [14.7, 8.5, 23.4],
  [15.8, 9.4, 25.1],
  [16.9, 10.4, 26.4],
  [17.9, 11.0, 27.0],
  [19.0, 11.7, 28.6],
  [20.1, 12.4, 30.0],
  [21.2, 13.4, 31.2],
  [22.2, 14.6, 32.3],
  [23.3, 15.7, 33.8],
  [24.4, 16.0, 35.0],
  [25.5, 17.2, 36.1],
  [26.6, 17.9, 37.5],
  [27.7, 19.2, 38.5],
  [28.8, 20.0, 39.9],
  [29.9, 20.7, 41.2],
  [31.0, 22.1, 42.3],
  [32.1, 22.9, 43.4],
  [33.2, 23.7, 44.9],
  [34.3, 24.4, 45.8],
  [35.4, 25.9, 47.2],
  [36.5, 26.7, 48.5],
  [37.6, 27.6, 49.5],
  [38.7, 28.4, 51.0],
  [39.9, 29.2, 52.0],
  [41.0, 30.8, 53.3],
  [42.1, 31.7, 54.5],
  [43.2, 32.5, 55.7],
  [44.4, 33.4, 57.0],
  [45.5, 35.1, 58.0],
  [46.6, 36.0, 59.3],
  [47.7, 36.8, 60.7],
  [48.9, 37.7, 61.8],
  [50.0, 38.6, 63.0],
  [51.2, 39.5, 64.2],
  [52.3, 41.4, 65.4],
  [53.5, 42.3, 66.6],
  [54.6, 43.2, 67.8],
  [55.8, 44.1, 69.0],
  [56.9, 45.0, 70.4],
  [58.1, 45.9, 71.7],
  [6.3, 2.9, 13.7],
  [7.4, 3.2, 14.4],
  [8.4, 3.7, 15.3],
  [9.5, 4.4, 16.9],
  [10.6, 5.3, 18.5],
  [11.6, 6.0, 20.1],
  [12.7, 6.8, 21.7],
  [13.8, 7.7, 22.2],
  [14.9, 8.6, 23.6],
  [16.0, 9.5, 25.1],
  [17.0, 10.5, 26.8],
  [18.1, 11.1, 27.5],
  [19.2, 11.8, 28.8],
  [20.3, 12.5, 30.4],
  [21.4, 13.6, 31.8],
  [22.5, 14.7, 32.6],
  [23.6, 15.9, 34.2],
  [24.7, 16.6, 35.2],
  [25.8, 17.4, 36.5],
  [26.9, 18.1, 37.8],
  [28.0, 19.4, 39.1],
  [29.1, 20.2, 40.2],
  [30.2, 20.9, 41.6],
  [31.3, 22.3, 42.7],
  [32.4, 23.1, 44.2],
  [33.5, 23.9, 45.2],
  [34.7, 24.7, 46.5],
  [35.8, 26.2, 47.9],
  [36.9, 27.0, 48.8],
  [38.0, 27.9, 50.3],
  [39.2, 28.7, 51.2],
  [40.3, 29.5, 52.8],
  [41.4, 31.1, 53.8],
  [42.6, 32.0, 55.4],
  [43.7, 32.9, 56.2],
  [44.8, 33.7, 57.6],
  [46.0, 35.5, 58.9],
  [47.1, 36.4, 60.1],
  [48.3, 37.2, 61.3],
  [49.4, 38.1, 62.5],
  [50.6, 39.0, 63.6],
  [51.7, 39.9, 64.8],
  [52.9, 40.8, 66.1],
  [54.1, 42.8, 67.5],
  [55.2, 43.7, 68.7],
  [56.4, 44.6, 69.8],
  [57.6, 45.5, 71.0],
  [58.7, 46.5, 72.4],
  [59.9, 47.4, 73.7],
  [7.5, 3.6, 14.9],
  [8.5, 3.9, 15.6],
  [9.6, 4.4, 16.9],
  [10.7, 5.1, 18.5],
  [11.8, 6.1, 20.1],
  [12.8, 6.9, 21.7],
  [13.9, 7.8, 22.9],
  [15.0, 8.7, 24.0],
  [16.1, 9.6, 25.3],
  [17.2, 10.6, 26.9],
  [18.3, 11.6, 28.2],
  [19.4, 12.3, 29.3],
  [20.5, 12.6, 30.5],
  [21.6, 13.7, 31.9],
  [22.7, 14.9, 33.1],
  [23.8, 15.6, 34.5],
  [24.9, 16.8, 35.7],
  [26.0, 17.5, 37.1],
  [27.1, 18.3, 38.1],
  [28.3, 19.6, 39.7],
  [29.4, 20.4, 40.6],
  [30.5, 21.2, 42.0],
  [31.6, 21.9, 43.4],
  [32.8, 23.4, 44.4],
  [33.9, 24.2, 45.7],
  [35.0, 25.0, 47.2],
  [36.2, 26.5, 48.3],
  [37.3, 27.3, 49.5],
  [38.4, 28.2, 51.0],
  [39.6, 29.0, 52.0],
  [40.7, 30.6, 53.5],
  [41.9, 31.5, 54.5],
  [43.0, 32.4, 55.9],
  [44.2, 33.2, 57.1],
  [45.3, 34.1, 58.3],
  [46.5, 35.0, 59.7],
  [47.7, 36.8, 60.8],
  [48.8, 37.7, 61.9],
  [50.0, 38.6, 63.4],
  [51.2, 39.5, 64.7],
  [52.3, 40.4, 66.0],
  [53.5, 41.3, 67.3],
  [54.7, 43.3, 68.5],
  [55.9, 44.2, 69.7],
  [57.1, 45.1, 70.8],
  [58.3, 46.1, 72.2],
  [59.4, 47.0, 73.4],
  [60.6, 48.0, 74.4],
  [61.8, 48.9, 75.9],
  [8.6, 4.5, 16.9],
  [9.7, 4.5, 17.2],
  [10.8, 5.2, 18.6],
  [11.9, 5.9, 20.2],
  [13.0, 6.7, 21.8],
  [14.1, 7.8, 23.4],
  [15.2, 8.7, 24.6],
  [16.3, 9.7, 25.7],
  [17.4, 10.4, 26.9],
  [18.5, 11.4, 28.6],
  [19.6, 12.4, 30.0],
  [20.7, 13.1, 30.7],
  [21.8, 13.9, 32.3],
  [22.9, 14.6, 33.8],
  [24.1, 15.7, 35.0],
  [25.2, 17.0, 36.1],
  [26.3, 17.7, 37.7],
  [27.4, 18.5, 38.5],
  [28.6, 19.2, 40.2],
  [29.7, 20.6, 41.3],
  [30.8, 21.4, 42.7],
  [32.0, 22.2, 44.0],
  [33.1, 23.6, 44.9],
  [34.3, 24.4, 46.5],
  [35.4, 25.2, 47.8],
  [36.6, 26.1, 48.8],
  [37.7, 27.6, 50.3],
  [38.9, 28.5, 51.2],
  [40.0, 29.3, 52.8],
  [41.2, 30.2, 53.8],
  [42.3, 31.8, 55.4],
  [43.5, 32.7, 56.4],
  [44.7, 33.6, 58.0],
  [45.9, 34.5, 59.0],
  [47.0, 35.4, 60.2],
  [48.2, 36.3, 61.6],
  [49.4, 38.1, 63.0],
  [50.6, 39.0, 64.2],
  [51.8, 39.9, 65.4],
  [53.0, 40.9, 66.6],
  [54.1, 41.8, 67.8],
  [55.3, 43.8, 69.1],
  [56.5, 44.7, 70.4],
  [57.7, 45.7, 71.7],
  [59.0, 46.6, 73.0],
  [60.2, 47.6, 74.3],
  [61.4, 48.5, 75.7],
  [62.6, 49.5, 77.0],
  [63.8, 50.5, 77.9],
  [9.8, 4.7, 18.4],
  [10.9, 5.6, 19.5],
  [12.0, 6.0, 20.3],
  [13.1, 6.8, 21.8],
  [14.2, 7.6, 23.4],
  [15.3, 8.5, 25.1],
  [16.4, 9.5, 26.4],
  [17.6, 10.5, 27.0],
  [18.7, 11.5, 28.6],
  [19.8, 12.6, 30.4],
  [20.9, 13.3, 31.2],
  [22.0, 14.0, 32.6],
  [23.2, 14.7, 34.2],
  [24.3, 15.9, 35.2],
  [25.4, 17.1, 36.5],
  [26.6, 17.9, 38.1],
  [27.7, 18.7, 39.2],
  [28.9, 19.5, 40.6],
  [30.0, 20.8, 41.8],
  [31.2, 21.6, 43.2],
  [32.3, 22.4, 44.4],
  [33.5, 23.2, 45.7],
  [34.6, 24.7, 47.2],
  [35.8, 25.5, 48.0],
  [37.0, 26.3, 49.5],
  [38.1, 27.9, 51.0],
  [39.3, 28.8, 52.0],
  [40.5, 29.6, 53.7],
  [41.6, 30.5, 54.5],
  [42.8, 32.2, 56.1],
  [44.0, 33.1, 57.1],
  [45.2, 34.0, 58.6],
  [46.4, 34.9, 59.8],
  [47.6, 35.8, 61.2],
  [48.8, 36.7, 62.5],
  [50.0, 38.6, 63.6],
  [51.2, 39.5, 64.7],
  [52.4, 40.4, 66.1],
  [53.6, 41.3, 67.5],
  [54.8, 42.3, 69.0],
  [56.0, 44.3, 70.3],
  [57.2, 45.3, 71.5],
  [58.4, 46.2, 72.5],
  [59.7, 47.2, 74.0],
  [60.9, 48.2, 75.4],
  [62.1, 49.1, 76.4],
  [63.4, 51.4, 77.8],
  [64.6, 51.1, 79.3],
  [65.8, 53.4, 80.6],
  [11.0, 5.7, 20.1],
  [12.1, 6.5, 21.1],
  [13.2, 7.1, 22.0],
  [14.4, 7.7, 23.6],
  [15.5, 8.6, 25.1],
  [16.6, 9.6, 26.8],
  [17.7, 10.6, 27.5],
  [18.9, 11.6, 28.8],
  [20.0, 12.7, 30.4],
  [21.1, 13.4, 31.8],
  [22.3, 14.1, 33.1],
  [23.4, 14.9, 34.5],
  [24.6, 16.1, 35.7],
  [25.7, 16.8, 37.1],
  [26.9, 18.1, 38.5],
  [28.0, 18.9, 39.7],
  [29.2, 19.7, 41.2],
  [30.3, 21.0, 42.3],
  [31.5, 21.8, 43.4],
  [32.7, 23.3, 44.9],
  [33.8, 23.5, 46.5],
  [35.0, 25.0, 47.5],
  [36.2, 25.8, 48.8],
  [37.4, 26.6, 50.3],
  [38.6, 28.2, 51.2],
  [39.7, 29.1, 52.8],
  [40.9, 30.0, 54.1],
  [42.1, 30.9, 55.4],
  [43.3, 31.7, 56.8],
  [44.5, 33.5, 58.0],
  [45.7, 34.4, 59.1],
  [46.9, 35.3, 60.7],
  [48.1, 36.2, 61.8],
  [49.3, 37.1, 63.0],
  [50.6, 39.0, 64.5],
  [51.8, 40.0, 66.0],
  [53.0, 40.9, 67.3],
  [54.2, 41.8, 68.5],
  [55.5, 42.8, 69.7],
  [56.7, 43.7, 71.0],
  [57.9, 45.8, 72.3],
  [59.2, 46.8, 73.7],
  [60.4, 47.8, 75.0],
  [61.7, 48.8, 76.3],
  [62.9, 49.8, 77.6],
  [64.2, 50.7, 79.2],
  [65.4, 53.0, 80.0],
  [66.7, 54.0, 81.4],
  [67.9, 55.1, 83.2],
  [12.2, 6.8, 21.4],
  [13.4, 7.4, 22.3],
  [14.5, 7.8, 23.6],
  [15.6, 8.7, 25.3],
  [16.8, 9.7, 26.8],
  [17.9, 10.7, 28.2],
  [19.1, 11.4, 29.3],
  [20.2, 12.4, 30.7],
  [21.4, 13.6, 32.3],
  [22.5, 14.3, 33.8],
  [23.7, 15.5, 35.0],
  [24.8, 15.8, 36.1],
  [26.0, 17.0, 37.7],
  [27.2, 18.3, 39.1],
  [28.3, 19.1, 40.2],
  [29.5, 19.9, 41.6],
  [30.7, 20.7, 42.7],
  [31.9, 22.1, 44.2],
  [33.0, 23.6, 45.7],
  [34.2, 23.7, 46.8],
  [35.4, 25.2, 48.0],
  [36.6, 26.1, 49.5],
  [37.8, 26.9, 51.0],
  [39.0, 28.6, 52.0],
  [40.2, 29.4, 53.7],
  [41.4, 30.3, 54.6],
  [42.6, 31.2, 56.2],
  [43.8, 32.1, 57.4],
  [45.0, 33.9, 58.9],
  [46.3, 34.8, 60.1],
  [47.5, 35.7, 61.3],
  [48.7, 36.6, 62.6],
  [49.9, 38.5, 64.2],
  [51.2, 39.5, 65.4],
  [52.4, 40.4, 66.6],
  [53.7, 41.4, 67.8],
  [54.9, 42.4, 69.2],
  [56.1, 43.3, 70.7],
  [57.4, 45.4, 72.2],
  [58.6, 45.2, 73.4],
  [59.9, 47.4, 74.5],
  [61.2, 48.4, 75.9],
  [62.4, 49.4, 77.6],
  [63.7, 50.4, 78.6],
  [65.0, 51.4, 80.0],
  [66.3, 52.4, 81.4],
  [67.5, 54.7, 83.0],
  [68.8, 55.8, 83.7],
  [70.1, 56.8, 85.3],
  [13.5, 7.8, 23.4],
  [14.6, 8.2, 24.6],
  [15.8, 8.8, 25.7],
  [16.9, 9.4, 27.0],
  [18.1, 10.4, 28.6],
  [19.3, 11.5, 30.0],
  [20.4, 12.6, 31.2],
  [21.6, 13.7, 32.6],
  [22.8, 14.9, 34.2],
  [23.9, 15.7, 35.2],
  [25.1, 15.9, 36.5],
  [26.3, 17.2, 38.1],
  [27.5, 18.0, 39.2],
  [28.6, 19.3, 40.6],
  [29.8, 20.1, 42.0],
  [31.0, 21.5, 43.4],
  [32.2, 22.3, 44.9],
  [33.4, 23.2, 46.2],
  [34.6, 24.7, 47.2],
  [35.8, 24.8, 48.8],
  [37.0, 26.4, 50.3],
  [38.2, 27.3, 51.2],
  [39.5, 28.9, 52.8],
  [40.7, 29.8, 54.4],
  [41.9, 30.7, 55.4],
  [43.1, 31.6, 57.0],
  [44.3, 32.5, 58.0],
  [45.6, 33.4, 59.7],
  [46.8, 35.2, 60.8],
  [48.1, 36.1, 62.5],
  [49.3, 37.1, 63.6],
  [50.6, 38.0, 64.7],
  [51.8, 40.0, 66.1],
  [53.1, 40.9, 67.7],
  [54.3, 41.9, 69.0],
  [55.6, 42.9, 70.4],
  [56.8, 43.9, 71.7],
  [58.1, 44.8, 73.0],
  [59.4, 47.0, 74.4],
  [60.7, 48.0, 75.8],
  [62.0, 49.0, 77.2],
  [63.2, 50.0, 78.6],
  [64.5, 51.0, 79.5],
  [65.8, 52.1, 81.4],
  [67.1, 53.1, 82.7],
  [68.4, 54.1, 83.7],
  [69.7, 56.5, 85.3],
  [71.0, 57.6, 86.9],
  [72.4, 58.6, 87.7],
  [14.8, 8.5, 25.1],
  [16.0, 9.2, 26.4],
  [17.1, 9.9, 27.4],
  [18.3, 10.5, 28.8],
  [19.5, 11.6, 30.4],
  [20.6, 12.7, 31.8],
  [21.8, 13.4, 33.1],
  [23.0, 14.6, 34.5],
  [24.2, 15.8, 35.7],
  [25.4, 16.6, 37.1],
  [26.6, 17.4, 38.5],
  [27.8, 18.2, 39.9],
  [29.0, 19.5, 41.3],
  [30.2, 20.3, 42.7],
  [31.4, 21.8, 44.2],
  [32.6, 22.6, 45.6],
  [33.8, 23.4, 46.6],
  [35.0, 25.0, 48.0],
  [36.2, 25.8, 49.5],
  [37.5, 26.7, 51.0],
  [38.7, 27.6, 52.0],
  [39.9, 28.5, 53.7],
  [41.2, 30.1, 54.7],
  [42.4, 31.1, 56.2],
  [43.6, 32.0, 57.6],
  [44.9, 32.9, 58.9],
  [46.1, 34.7, 60.2],
  [47.4, 35.6, 61.6],
  [48.6, 36.6, 63.0],
  [49.9, 37.5, 64.4],
  [51.2, 38.5, 66.0],
  [52.5, 39.4, 67.3],
  [53.7, 41.4, 68.5],
  [55.0, 42.4, 69.7],
  [56.3, 43.4, 71.0],
  [57.6, 44.4, 72.4],
  [58.9, 45.4, 74.0],
  [60.2, 46.4, 75.7],
  [61.5, 48.6, 76.9],
  [62.8, 49.6, 77.9],
  [64.1, 50.7, 79.5],
  [65.4, 51.7, 81.2],
  [66.7, 52.8, 82.3],
  [68.0, 53.8, 83.4],
  [69.3, 54.9, 85.3],
  [70.7, 55.9, 86.7],
  [72.0, 58.4, 87.4],
  [73.3, 59.4, 89.5],
  [74.7, 60.5, 90.8],
  [16.1, 9.3, 26.8],
  [17.3, 10.3, 28.2],
  [18.5, 11.0, 29.2],
  [19.7, 11.7, 30.7],
  [20.9, 12.4, 32.3],
  [22.1, 13.6, 33.7],
  [23.3, 14.8, 35.0],
  [24.5, 16.0, 36.1],
  [25.7, 16.8, 37.7],
  [26.9, 17.6, 39.1],
  [28.1, 18.4, 40.6],
  [29.3, 19.7, 41.8],
  [30.5, 20.6, 43.4],
  [31.7, 22.0, 44.4],
  [33.0, 22.9, 45.8],
  [34.2, 23.7, 47.2],
  [35.4, 24.6, 48.8],
  [36.7, 26.1, 50.3],
  [37.9, 27.0, 51.2],
  [39.1, 27.9, 52.8],
  [40.4, 28.8, 54.5],
  [41.6, 30.5, 55.4],
  [42.9, 31.4, 57.1],
  [44.2, 32.4, 58.6],
  [45.4, 33.3, 59.8],
  [46.7, 35.1, 61.3],
  [48.0, 36.1, 62.5],
  [49.3, 37.0, 64.0],
  [50.5, 38.0, 65.4],
  [51.8, 39.0, 66.6],
  [53.1, 41.0, 67.9],
  [54.4, 42.0, 69.6],
  [55.7, 43.0, 70.9],
  [57.0, 44.0, 72.3],
  [58.3, 45.0, 73.7],
  [59.6, 46.0, 75.0],
  [60.9, 47.0, 76.4],
  [62.3, 49.3, 77.8],
  [63.6, 50.3, 79.5],
  [64.9, 51.4, 80.8],
  [66.3, 52.4, 82.1],
  [67.6, 53.5, 83.3],
  [68.9, 54.5, 85.3],
  [70.3, 55.6, 86.3],
  [71.6, 56.7, 87.4],
  [73.0, 59.2, 89.5],
  [74.4, 60.3, 90.7],
  [75.7, 61.4, 91.6],
  [77.1, 62.5, 93.8],
  [17.5, 10.1, 28.6],
  [18.7, 10.8, 30.0],
  [19.9, 11.9, 30.9],
  [21.1, 12.6, 32.6],
  [22.3, 13.7, 34.1],
  [23.5, 14.9, 35.2],
  [24.7, 15.7, 36.5],
  [25.9, 17.0, 38.1],
  [27.2, 17.8, 39.7],
  [28.4, 18.6, 41.2],
  [29.6, 19.4, 42.3],
  [30.9, 20.8, 44.0],
  [32.1, 21.6, 44.9],
  [33.3, 23.1, 46.5],
  [34.6, 24.0, 48.0],
  [35.8, 24.9, 49.5],
  [37.1, 26.4, 51.0],
  [38.4, 27.3, 52.0],
  [39.6, 28.2, 53.7],
  [40.9, 29.1, 55.2],
  [42.2, 30.9, 56.2],
  [43.4, 31.8, 58.0],
  [44.7, 32.8, 59.0],
  [46.0, 33.7, 60.7],
  [47.3, 35.6, 61.8],
  [48.6, 36.5, 63.5],
  [49.9, 37.5, 64.7],
  [51.2, 38.5, 66.1],
  [52.5, 39.5, 67.8],
  [53.8, 40.5, 69.0],
  [55.1, 41.4, 70.4],
  [56.4, 43.5, 72.0],
  [57.8, 44.6, 73.4],
  [59.1, 45.6, 74.7],
  [60.4, 46.6, 75.9],
  [61.8, 47.7, 77.6],
  [63.1, 48.7, 79.2],
  [64.5, 51.0, 80.6],
  [65.8, 52.1, 81.5],
  [67.2, 53.1, 83.3],
  [68.5, 54.2, 85.1],
  [69.9, 55.3, 86.2],
  [71.3, 56.4, 87.3],
  [72.6, 58.9, 89.5],
  [74.0, 60.0, 90.5],
  [75.4, 61.1, 91.6],
  [76.8, 62.3, 93.8],
  [78.2, 63.4, 94.6],
  [79.6, 64.5, 96.1],
  [18.9, 11.3, 30.4],
  [20.1, 12.4, 31.8],
  [21.3, 12.7, 32.6],
  [22.6, 13.9, 34.5],
  [23.8, 14.6, 35.8],
  [25.0, 15.9, 37.1],
  [26.2, 17.2, 38.5],
  [27.5, 18.0, 40.2],
  [28.7, 18.8, 41.6],
  [30.0, 19.6, 42.7],
  [31.2, 21.0, 44.4],
  [32.5, 21.9, 45.7],
  [33.7, 23.4, 47.2],
  [35.0, 24.3, 48.8],
  [36.3, 25.2, 50.3],
  [37.5, 26.8, 51.2],
  [38.8, 27.7, 52.8],
  [40.1, 28.6, 54.5],
  [41.4, 29.5, 55.9],
  [42.7, 30.4, 57.1],
  [44.0, 32.2, 58.6],
  [45.3, 33.2, 60.1],
  [46.6, 34.1, 61.5],
  [47.9, 35.1, 63.0],
  [49.2, 37.0, 64.2],
  [50.5, 38.0, 66.0],
  [51.8, 39.0, 67.3],
  [53.2, 40.0, 68.5],
  [54.5, 41.0, 70.3],
  [55.8, 43.1, 71.7],
  [57.2, 44.1, 73.0],
  [58.5, 45.2, 74.4],
  [59.9, 46.2, 75.9],
  [61.2, 47.3, 77.6],
  [62.6, 48.3, 78.9],
  [64.0, 49.4, 80.0],
  [65.3, 50.4, 81.4],
  [66.7, 52.8, 83.3],
  [68.1, 53.9, 84.8],
  [69.5, 55.0, 85.9],
  [70.9, 56.1, 87.3],
  [72.3, 57.2, 89.5],
  [73.7, 58.3, 90.3],
  [75.1, 60.9, 91.6],
  [76.5, 62.0, 93.8],
  [77.9, 63.2, 94.6],
  [79.3, 64.3, 96.1],
  [80.8, 65.5, 98.4],
  [82.2, 66.6, 98.9],
  [20.3, 12.1, 32.2],
  [21.6, 12.9, 33.7],
  [22.8, 14.0, 35.0],
  [24.1, 14.8, 36.5],
  [25.3, 16.1, 37.7],
  [26.6, 16.9, 39.2],
  [27.8, 18.2, 40.6],
  [29.1, 19.0, 42.0],
  [30.3, 20.4, 43.4],
  [31.6, 21.3, 44.9],
  [32.9, 22.2, 46.5],
  [34.1, 23.7, 48.0],
  [35.4, 24.6, 49.5],
  [36.7, 25.5, 51.0],
  [38.0, 26.4, 52.0],
  [39.3, 28.0, 53.7],
  [40.6, 28.9, 55.4],
  [41.9, 29.9, 56.4],
  [43.2, 30.8, 58.0],
  [44.5, 32.6, 59.7],
  [45.9, 33.6, 60.8],
  [47.2, 34.6, 62.5],
  [48.5, 35.5, 63.6],
  [49.8, 37.5, 65.4],
  [51.2, 38.5, 66.6],
  [52.5, 39.5, 68.4],
  [53.9, 40.5, 69.7],
  [55.2, 41.5, 71.0],
  [56.6, 42.6, 72.4],
  [58.0, 44.7, 74.0],
  [59.3, 45.8, 75.7],
  [60.7, 46.8, 77.2],
  [62.1, 47.9, 78.6],
  [63.5, 49.0, 79.8],
  [64.9, 51.3, 81.4],
  [66.3, 51.1, 83.3],
  [67.7, 53.5, 84.6],
  [69.1, 54.6, 85.4],
  [70.5, 55.8, 87.3],
  [71.9, 56.9, 89.4],
  [73.3, 58.0, 90.3],
  [74.8, 59.1, 91.6],
  [76.2, 60.3, 93.9],
  [77.6, 62.9, 94.6],
  [79.1, 64.1, 96.1],
  [80.5, 65.3, 98.6],
  [82.0, 66.5, 99.0],
  [83.5, 67.7, 101.0],
  [84.9, 68.8, 102.5],
  [21.8, 13.4, 33.9],
  [23.1, 14.2, 35.7],
  [24.3, 15.4, 37.1],
  [25.6, 15.7, 38.4],
  [26.9, 17.1, 39.8],
  [28.1, 18.4, 41.3],
  [29.4, 19.2, 42.7],
  [30.7, 20.7, 44.2],
  [32.0, 21.6, 45.7],
  [33.3, 22.4, 47.2],
  [34.6, 23.3, 48.8],
  [35.9, 24.9, 50.3],
  [37.2, 25.8, 51.2],
  [38.5, 26.7, 52.8],
  [39.8, 27.6, 54.5],
  [41.1, 29.3, 56.1],
  [42.4, 30.3, 57.4],
  [43.8, 31.2, 58.9],
  [45.1, 33.0, 60.2],
  [46.5, 34.0, 61.8],
  [47.8, 35.0, 63.4],
  [49.2, 36.0, 64.7],
  [50.5, 38.0, 66.1],
  [51.9, 39.0, 67.8],
  [53.2, 40.0, 69.1],
  [54.6, 41.1, 70.7],
  [56.0, 42.1, 72.2],
  [57.4, 43.1, 73.7],
  [58.8, 45.3, 75.1],
  [60.2, 46.4, 76.6],
  [61.6, 47.5, 77.9],
  [63.0, 48.6, 79.5],
  [64.4, 49.7, 81.4],
  [65.8, 50.8, 83.0],
  [67.2, 53.2, 84.2],
  [68.6, 53.0, 85.3],
  [70.1, 55.4, 87.3],
  [71.5, 56.6, 89.2],
  [73.0, 57.7, 90.0],
  [74.4, 58.9, 91.6],
  [75.9, 60.0, 93.9],
  [77.3, 61.2, 94.6],
  [78.8, 63.9, 96.1],
  [80.3, 65.1, 98.6],
  [81.8, 66.3, 99.2],
  [83.3, 67.5, 101.0],
  [84.8, 68.7, 102.8],
  [86.3, 69.9, 103.8],
  [87.8, 71.1, 106.2],
  [23.3, 14.4, 36.1],
  [24.6, 15.1, 37.6],
  [25.9, 16.4, 39.1],
  [27.2, 17.3, 40.2],
  [28.5, 18.1, 41.8],
  [29.8, 19.5, 43.4],
  [31.1, 20.9, 44.9],
  [32.4, 21.8, 46.5],
  [33.7, 22.7, 48.0],
  [35.0, 23.6, 49.5],
  [36.3, 25.2, 51.0],
  [37.6, 26.1, 52.0],
  [39.0, 27.0, 53.7],
  [40.3, 27.9, 55.4],
  [41.6, 29.7, 57.0],
  [43.0, 30.7, 58.0],
  [44.3, 31.6, 59.8],
  [45.7, 32.6, 61.3],
  [47.1, 34.5, 62.6],
  [48.4, 35.5, 64.2],
  [49.8, 36.5, 66.0],
  [51.2, 37.5, 67.3],
  [52.6, 39.5, 69.0],
  [54.0, 40.6, 70.4],
  [55.4, 41.6, 71.7],
  [56.8, 42.7, 73.4],
  [58.2, 43.7, 75.0],
  [59.6, 44.8, 76.4],
  [61.0, 47.1, 77.8],
  [62.4, 48.2, 79.5],
  [63.9, 49.3, 81.2],
  [65.3, 50.4, 82.7],
  [66.8, 51.5, 83.7],
  [68.2, 54.0, 85.3],
  [69.7, 53.7, 87.3],
  [71.1, 56.3, 89.1],
  [72.6, 57.4, 89.8],
  [74.1, 58.6, 91.6],
  [75.5, 59.8, 93.9],
  [77.0, 60.9, 94.6],
  [78.5, 62.1, 96.1],
  [80.0, 64.9, 98.6],
  [81.5, 66.1, 99.5],
  [83.1, 67.3, 101.0],
  [84.6, 68.6, 103.3],
  [86.1, 69.8, 103.9],
  [87.6, 71.0, 106.2],
  [89.2, 72.3, 107.6],
  [90.7, 73.5, 109.1],
  [24.9, 15.8, 38.1],
  [26.2, 16.6, 39.7],
  [27.5, 17.5, 41.2],
  [28.8, 18.3, 42.7],
  [30.1, 19.7, 44.2],
  [31.5, 20.6, 45.7],
  [32.8, 22.1, 47.2],
  [34.1, 23.0, 48.8],
  [35.4, 23.9, 50.3],
  [36.8, 25.5, 51.2],
  [38.1, 26.4, 52.8],
  [39.5, 27.4, 54.5],
  [40.8, 28.3, 56.2],
  [42.2, 30.1, 57.6],
  [43.6, 31.1, 59.0],
  [44.9, 32.0, 60.7],
  [46.3, 33.0, 62.5],
  [47.7, 34.9, 63.6],
  [49.1, 36.0, 65.4],
  [50.5, 37.0, 66.6],
  [51.9, 38.0, 68.5],
  [53.3, 40.1, 69.7],
  [54.7, 41.1, 71.5],
  [56.1, 42.2, 73.0],
  [57.6, 43.3, 74.4],
  [59.0, 44.4, 75.9],
  [60.4, 45.5, 77.6],
  [61.9, 47.8, 79.3],
  [63.3, 48.9, 80.8],
  [64.8, 50.0, 82.3],
  [66.3, 51.1, 83.4],
  [67.7, 52.3, 85.3],
  [69.2, 53.4, 87.3],
  [70.7, 55.9, 88.7],
  [72.2, 57.1, 89.6],
  [73.7, 58.3, 91.6],
  [75.2, 59.5, 93.9],
  [76.7, 60.7, 94.6],
  [78.2, 61.9, 96.1],
  [79.8, 63.1, 98.6],
  [81.3, 64.3, 99.5],
  [82.8, 65.5, 101.0],
  [84.4, 68.4, 103.3],
  [85.9, 69.7, 104.4],
  [87.5, 70.9, 106.2],
  [89.1, 72.2, 108.0],
  [90.7, 73.5, 109.1],
  [92.2, 74.8, 111.4],
  [93.8, 76.1, 112.8],
  [26.5, 16.8, 40.1],
  [27.9, 18.2, 41.3],
  [29.2, 18.5, 43.1],
  [30.5, 19.4, 44.8],
  [31.8, 20.8, 46.4],
  [33.2, 21.7, 47.9],
  [34.5, 23.3, 49.5],
  [35.9, 24.2, 51.0],
  [37.3, 25.8, 52.0],
  [38.6, 26.8, 53.7],
  [40.0, 27.7, 55.4],
  [41.4, 28.7, 57.1],
  [42.8, 30.5, 58.6],
  [44.1, 31.5, 60.1],
  [45.5, 32.5, 61.6],
  [46.9, 33.5, 63.0],
  [48.4, 35.4, 64.7],
  [49.8, 36.5, 66.1],
  [51.2, 37.5, 67.8],
  [52.6, 38.5, 69.2],
  [54.1, 39.6, 70.9],
  [55.5, 41.7, 72.4],
  [56.9, 42.8, 74.0],
  [58.4, 43.9, 75.8],
  [59.9, 45.0, 77.6],
  [61.3, 46.1, 79.2],
  [62.8, 48.5, 80.6],
  [64.3, 49.6, 81.7],
  [65.8, 50.7, 83.4],
  [67.3, 51.9, 85.3],
  [68.8, 53.1, 87.2],
  [70.3, 54.2, 88.6],
  [71.8, 56.8, 89.5],
  [73.3, 58.0, 91.6],
  [74.9, 59.2, 93.9],
  [76.4, 60.4, 94.6],
  [77.9, 61.7, 96.1],
  [79.5, 62.9, 98.6],
  [81.1, 64.1, 99.8],
  [82.6, 65.4, 101.0],
  [84.2, 68.2, 103.6],
  [85.8, 69.5, 104.7],
  [87.4, 70.8, 106.2],
  [89.0, 72.1, 108.3],
  [90.6, 73.4, 109.4],
  [92.2, 74.7, 111.8],
  [93.8, 76.0, 112.9],
  [95.4, 79.2, 114.9],
  [97.1, 78.7, 116.4],
  [28.2, 17.9, 42.3],
  [29.5, 18.8, 44.0],
  [30.9, 19.6, 45.5],
  [32.3, 21.1, 47.2],
  [33.6, 22.0, 48.8],
  [35.0, 23.6, 50.3],
  [36.4, 24.5, 51.3],
  [37.7, 26.2, 52.9],
  [39.1, 27.1, 54.5],
  [40.5, 28.1, 56.2],
  [41.9, 29.1, 58.0],
  [43.3, 30.9, 59.7],
  [44.8, 31.9, 60.8],
  [46.2, 32.9, 62.5],
  [47.6, 33.9, 64.2],
  [49.0, 35.0, 66.0],
  [50.5, 37.0, 67.3],
  [51.9, 38.0, 69.0],
  [53.4, 39.1, 70.4],
  [54.8, 40.2, 72.2],
  [56.3, 41.2, 73.7],
  [57.8, 43.5, 75.7],
  [59.3, 44.6, 77.2],
  [60.8, 45.7, 78.6],
  [62.3, 46.8, 80.0],
  [63.8, 47.9, 81.5],
  [65.3, 50.4, 83.3],
  [66.8, 51.5, 85.3],
  [68.3, 52.7, 87.0],
  [69.8, 53.9, 88.2],
  [71.4, 55.1, 89.5],
  [72.9, 56.3, 91.6],
  [74.5, 58.9, 93.9],
  [76.1, 60.2, 94.6],
  [77.6, 61.4, 96.1],
  [79.2, 62.7, 98.6],
  [80.8, 63.9, 100.1],
  [82.4, 65.2, 101.0],
  [84.0, 66.4, 103.6],
  [85.6, 69.4, 105.0],
  [87.2, 70.7, 106.4],
  [88.9, 72.0, 108.8],
  [90.5, 73.4, 110.0],
  [92.1, 74.7, 111.8],
  [93.8, 76.0, 113.8],
  [95.5, 77.4, 115.1],
  [97.1, 78.7, 117.3],
  [98.8, 82.0, 118.7],
  [100.5, 83.4, 120.4],
  [29.9, 19.0, 44.4],
  [31.3, 20.5, 46.0],
  [32.7, 21.4, 47.7],
  [34.1, 22.3, 49.2],
  [35.5, 23.9, 51.0],
  [36.8, 24.8, 52.2],
  [38.3, 25.8, 53.8],
  [39.7, 26.7, 55.4],
  [41.1, 28.5, 57.1],
  [42.5, 29.5, 58.9],
  [43.9, 30.5, 60.2],
  [45.4, 32.3, 61.8],
  [46.8, 33.4, 63.6],
  [48.3, 34.4, 65.4],
  [49.7, 35.5, 66.6],
  [51.2, 37.5, 68.5],
  [52.7, 38.6, 70.3],
  [54.2, 39.7, 71.7],
  [55.6, 40.8, 73.4],
  [57.1, 41.9, 75.0],
  [58.6, 43.0, 76.4],
  [60.2, 45.2, 77.9],
  [61.7, 46.4, 79.5],
  [63.2, 47.5, 81.4],
  [64.7, 48.7, 83.3],
  [66.3, 51.1, 85.3],
  [67.8, 51.0, 86.9],
  [69.4, 53.5, 87.8],
  [71.0, 54.7, 89.5],
  [72.5, 56.0, 91.6],
  [74.1, 57.2, 93.9],
  [75.7, 58.4, 94.6],
  [77.3, 61.2, 96.1],
  [78.9, 62.4, 98.6],
  [80.5, 63.7, 100.4],
  [82.2, 65.0, 101.2],
  [83.8, 66.3, 103.6],
  [85.4, 67.6, 105.6],
  [87.1, 68.9, 106.4],
  [88.7, 70.2, 109.0],
  [90.4, 73.3, 110.3],
  [92.1, 74.6, 112.2],
  [93.8, 76.0, 114.0],
  [95.5, 77.4, 115.4],
  [97.2, 78.8, 117.5],
  [98.9, 80.2, 119.2],
  [100.6, 81.6, 121.2],
  [102.4, 83.0, 123.1],
  [104.1, 86.4, 124.7],
  [31.7, 20.7, 46.6],
  [33.1, 21.7, 48.1],
  [34.5, 23.3, 50.1],
  [35.9, 24.2, 51.9],
  [37.3, 25.2, 53.3],
  [38.8, 26.1, 54.7],
  [40.2, 27.1, 56.4],
  [41.7, 28.9, 58.0],
  [43.1, 29.9, 59.8],
  [44.6, 30.9, 61.3],
  [46.0, 32.8, 63.0],
  [47.5, 33.9, 64.7],
  [49.0, 34.9, 66.1],
  [50.5, 36.0, 67.8],
  [52.0, 38.1, 69.7],
  [53.5, 39.2, 71.0],
  [55.0, 40.3, 73.0],
  [56.5, 41.4, 74.4],
  [58.0, 42.5, 75.9],
  [59.5, 44.8, 77.8],
  [61.1, 45.9, 79.5],
  [62.6, 47.1, 81.4],
  [64.2, 48.3, 83.3],
  [65.8, 49.4, 85.1],
  [67.3, 50.6, 86.3],
  [68.9, 53.2, 87.7],
  [70.5, 54.4, 89.5],
  [72.1, 55.6, 91.6],
  [73.7, 56.9, 93.9],
  [75.3, 58.1, 94.6],
  [77.0, 60.9, 96.1],
  [78.6, 62.2, 98.6],
  [80.3, 63.5, 100.6],
  [81.9, 64.8, 101.5],
  [83.6, 66.1, 103.6],
  [85.2, 67.4, 105.7],
  [86.9, 68.8, 107.0],
  [88.6, 70.1, 109.0],
  [90.3, 71.4, 111.1],
  [92.0, 74.6, 112.2],
  [93.8, 76.0, 114.7],
  [95.5, 77.4, 116.4],
  [97.2, 78.8, 118.0],
  [99.0, 80.2, 120.1],
  [100.7, 81.7, 121.8],
  [102.5, 83.1, 123.4],
  [104.3, 86.6, 125.1],
  [106.1, 86.0, 127.3],
  [107.9, 89.6, 128.9],
  [33.6, 22.0, 48.9],
  [35.0, 22.9, 51.2],
  [36.4, 23.8, 52.6],
  [37.9, 25.5, 54.0],
  [39.3, 26.5, 55.9],
  [40.8, 28.3, 57.3],
  [42.2, 29.3, 59.0],
  [43.7, 30.3, 60.7],
  [45.2, 31.3, 62.5],
  [46.7, 33.3, 64.2],
  [48.2, 34.4, 66.0],
  [49.7, 35.4, 67.3],
  [51.2, 36.5, 69.0],
  [52.7, 37.6, 70.7],
  [54.3, 39.7, 72.4],
  [55.8, 40.9, 74.0],
  [57.3, 42.0, 75.9],
  [58.9, 43.1, 77.6],
  [60.5, 45.5, 79.5],
  [62.0, 46.7, 81.2],
  [63.6, 47.8, 83.0],
  [65.2, 49.0, 84.6],
  [66.8, 50.2, 86.2],
  [68.4, 51.5, 87.4],
  [70.0, 54.0, 89.5],
  [71.7, 55.3, 91.6],
  [73.3, 56.6, 93.9],
  [75.0, 57.8, 94.6],
  [76.6, 59.1, 96.1],
  [78.3, 60.4, 98.6],
  [80.0, 61.7, 101.0],
  [81.7, 64.6, 101.6],
  [83.3, 65.9, 103.6],
  [85.1, 67.3, 106.2],
  [86.8, 68.6, 107.3],
  [88.5, 70.0, 109.1],
  [90.2, 71.4, 111.4],
  [92.0, 72.8, 112.8],
  [93.7, 74.2, 114.9],
  [95.5, 77.4, 116.4],
  [97.3, 78.9, 118.3],
  [99.1, 80.3, 120.4],
  [100.9, 81.8, 121.9],
  [102.7, 83.2, 124.2],
  [104.5, 84.7, 126.0],
  [106.3, 86.2, 128.0],
  [108.2, 89.8, 130.2],
  [110.0, 91.3, 132.1],
  [111.9, 92.9, 133.7],
  [35.5, 23.2, 52.0],
  [36.9, 24.9, 53.7],
  [38.4, 25.9, 55.4],
  [39.9, 26.9, 56.5],
  [41.4, 27.9, 58.6],
  [42.8, 29.7, 60.1],
  [44.3, 30.7, 61.8],
  [45.9, 31.8, 63.5],
  [47.4, 32.8, 65.4],
  [48.9, 34.9, 66.6],
  [50.4, 36.0, 68.5],
  [52.0, 37.1, 70.4],
  [53.5, 38.2, 72.2],
  [55.1, 40.4, 73.7],
  [56.7, 41.5, 75.7],
  [58.2, 42.7, 77.6],
  [59.8, 43.8, 79.2],
  [61.4, 45.0, 80.8],
  [63.0, 46.2, 82.7],
  [64.7, 48.6, 84.2],
  [66.3, 49.8, 85.4],
  [67.9, 51.1, 87.3],
  [69.6, 52.3, 89.5],
  [71.2, 55.0, 91.6],
  [72.9, 54.8, 93.9],
  [74.6, 57.5, 94.6],
  [76.3, 58.8, 96.2],
  [78.0, 60.1, 98.6],
  [79.7, 61.5, 101.0],
  [81.4, 62.8, 102.2],
  [83.1, 64.1, 103.6],
  [84.9, 67.1, 106.2],
  [86.6, 68.5, 108.0],
  [88.4, 69.9, 109.4],
  [90.1, 71.3, 111.8],
  [91.9, 72.7, 113.5],
  [93.7, 74.1, 115.1],
  [95.5, 75.6, 117.5],
  [97.3, 78.9, 119.2],
  [99.2, 80.4, 121.2],
  [101.0, 81.9, 123.1],
  [102.9, 83.4, 125.0],
  [104.7, 84.9, 127.1],
  [106.6, 86.4, 128.9],
  [108.5, 90.1, 130.6],
  [110.4, 89.5, 132.4],
  [112.3, 93.2, 135.0],
  [114.2, 94.8, 137.0],
  [116.2, 96.4, 138.7],
  [37.4, 24.5, 54.5],
  [38.9, 26.2, 55.9],
  [40.4, 27.3, 57.4],
  [42.0, 28.3, 59.7],
  [43.5, 29.3, 60.8],
  [45.0, 31.2, 63.0],
  [46.5, 32.3, 64.7],
  [48.1, 33.3, 66.1],
  [49.6, 35.4, 67.8],
  [51.2, 36.5, 69.7],
  [52.8, 37.6, 71.7],
  [54.4, 38.8, 73.4],
  [56.0, 41.0, 75.0],
  [57.6, 42.2, 77.2],
  [59.2, 43.3, 78.6],
  [60.8, 44.5, 80.6],
  [62.4, 45.7, 82.3],
  [64.1, 46.9, 83.7],
  [65.7, 49.4, 85.3],
  [67.4, 50.7, 87.3],
  [69.1, 51.9, 89.5],
  [70.8, 53.2, 91.6],
  [72.5, 55.9, 93.9],
  [74.2, 55.8, 94.6],
  [75.9, 58.5, 96.2],
  [77.6, 59.9, 98.6],
  [79.4, 61.2, 101.0],
  [81.1, 62.6, 102.5],
  [82.9, 63.9, 103.8],
  [84.6, 65.3, 106.2],
  [86.4, 68.4, 108.3],
  [88.2, 69.8, 109.6],
  [90.0, 71.2, 111.8],
  [91.9, 72.7, 113.8],
  [93.7, 74.1, 115.4],
  [95.5, 75.6, 118.0],
  [97.4, 77.1, 119.8],
  [99.3, 80.5, 121.8],
  [101.2, 82.0, 123.7],
  [103.1, 83.5, 125.9],
  [105.0, 85.1, 127.6],
  [106.9, 86.6, 129.4],
  [108.8, 88.2, 132.0],
  [110.8, 89.8, 133.7],
  [112.7, 91.4, 135.3],
  [114.7, 95.2, 137.8],
  [116.7, 94.6, 140.1],
  [118.7, 98.5, 142.1],
  [120.7, 100.2, 144.2],
  [39.5, 25.9, 57.0],
  [41.0, 27.7, 58.8],
  [42.6, 28.7, 60.7],
  [44.1, 30.6, 62.5],
  [45.7, 30.8, 63.8],
  [47.3, 32.8, 65.9],
  [48.8, 33.9, 67.5],
  [50.4, 35.0, 69.1],
  [52.0, 37.1, 71.0],
  [53.6, 38.2, 73.0],
  [55.2, 39.4, 74.4],
  [56.9, 41.7, 76.4],
  [58.5, 41.7, 77.9],
  [60.2, 44.1, 80.0],
  [61.8, 45.3, 81.5],
  [63.5, 46.5, 83.4],
  [65.2, 47.7, 85.3],
  [66.9, 50.3, 87.3],
  [68.6, 51.6, 89.5],
  [70.3, 52.8, 91.6],
  [72.0, 54.1, 93.9],
  [73.7, 55.5, 94.6],
  [75.5, 56.8, 96.2],
  [77.3, 59.6, 98.6],
  [79.0, 61.0, 101.0],
  [80.8, 62.4, 102.8],
  [82.6, 63.7, 103.9],
  [84.4, 65.1, 106.2],
  [86.3, 66.5, 108.8],
  [88.1, 68.0, 110.3],
  [89.9, 71.1, 112.2],
  [91.8, 72.6, 114.7],
  [93.7, 74.1, 116.4],
  [95.6, 75.6, 118.3],
  [97.5, 77.1, 120.4],
  [99.4, 78.6, 121.9],
  [101.3, 80.1, 124.2],
  [103.3, 83.7, 126.0],
  [105.2, 85.3, 128.5],
  [107.2, 86.9, 130.6],
  [109.2, 88.5, 132.4],
  [111.2, 90.1, 135.0],
  [113.2, 91.7, 137.0],
  [115.2, 95.6, 138.7],
  [117.3, 95.0, 140.7],
  [119.3, 99.0, 143.0],
  [121.4, 98.4, 145.4],
  [123.5, 102.5, 147.9],
  [125.6, 104.2, 149.8],
  [41.7, 28.1, 59.8],
  [43.2, 29.1, 61.4],
  [44.8, 30.2, 63.4],
  [46.4, 31.3, 65.4],
  [48.0, 33.3, 67.2],
  [49.6, 34.4, 68.5],
  [51.2, 35.5, 70.7],
  [52.8, 36.6, 72.3],
  [54.5, 38.8, 74.1],
  [56.1, 40.0, 75.9],
  [57.8, 41.2, 77.8],
  [59.5, 42.4, 79.5],
  [61.2, 44.8, 81.4],
  [62.9, 46.0, 83.3],
  [64.6, 47.3, 85.3],
  [66.3, 48.6, 87.3],
  [68.0, 51.2, 89.5],
  [69.8, 51.1, 91.6],
  [71.5, 53.8, 93.9],
  [73.3, 55.1, 94.6],
  [75.1, 56.5, 96.2],
  [76.9, 57.8, 98.6],
  [78.7, 60.7, 101.0],
  [80.5, 62.1, 103.3],
  [82.4, 63.5, 104.7],
  [84.2, 65.0, 106.4],
  [86.1, 66.4, 109.0],
  [87.9, 67.8, 111.1],
  [89.8, 69.3, 112.8],
  [91.7, 72.6, 114.9],
  [93.7, 74.1, 116.9],
  [95.6, 75.6, 119.2],
  [97.5, 77.2, 121.2],
  [99.5, 78.7, 123.1],
  [101.5, 80.3, 125.0],
  [103.5, 81.8, 127.3],
  [105.5, 83.4, 129.1],
  [107.5, 87.1, 131.7],
  [109.5, 88.8, 133.7],
  [111.6, 90.4, 135.3],
  [113.7, 92.1, 138.1],
  [115.7, 93.8, 140.4],
  [117.8, 95.5, 142.2],
  [120.0, 99.6, 144.2],
  [122.1, 99.0, 146.3],
  [124.2, 103.1, 148.9],
  [126.4, 104.9, 151.7],
  [128.6, 106.7, 153.5],
  [130.8, 108.6, 155.9],
  [43.9, 29.6, 62.6],
  [45.5, 30.7, 64.5],
  [47.1, 31.8, 66.6],
  [48.7, 33.8, 68.0],
  [50.4, 34.9, 70.1],
  [52.0, 36.1, 72.2],
  [53.7, 38.3, 74.0],
  [55.4, 39.5, 75.8],
  [57.1, 40.7, 77.6],
  [58.8, 41.9, 79.5],
  [60.5, 43.1, 81.4],
  [62.2, 45.6, 83.3],
  [64.0, 46.8, 85.3],
  [65.7, 48.1, 87.3],
  [67.5, 49.4, 89.5],
  [69.3, 50.7, 91.6],
  [71.0, 52.0, 93.9],
  [72.9, 54.8, 94.6],
  [74.7, 56.1, 96.2],
  [76.5, 57.5, 98.6],
  [78.3, 58.9, 101.0],
  [80.2, 60.3, 103.6],
  [82.1, 63.3, 105.0],
  [84.0, 64.8, 106.4],
  [85.9, 66.2, 109.0],
  [87.8, 67.7, 111.4],
  [89.7, 69.2, 112.9],
  [91.7, 70.7, 115.1],
  [93.6, 74.1, 117.5],
  [95.6, 75.6, 119.2],
  [97.6, 77.2, 121.8],
  [99.6, 78.8, 124.2],
  [101.6, 80.4, 126.0],
  [103.7, 82.0, 128.2],
  [105.7, 83.6, 130.6],
  [107.8, 87.4, 132.1],
  [109.9, 89.1, 135.0],
  [112.0, 88.6, 137.0],
  [114.2, 92.5, 138.7],
  [116.3, 94.3, 141.6],
  [118.5, 96.0, 144.0],
  [120.6, 97.8, 145.9],
  [122.8, 99.6, 148.2],
  [125.1, 101.4, 150.3],
  [127.3, 105.7, 152.6],
  [129.5, 107.5, 155.5],
  [131.8, 109.4, 157.6],
  [134.1, 111.3, 159.8],
  [136.4, 113.2, 162.7],
  [46.2, 31.1, 65.5],
  [47.9, 32.3, 67.5],
  [49.5, 34.4, 69.3],
  [51.2, 35.5, 71.7],
  [52.9, 36.7, 73.7],
  [54.6, 37.9, 75.6],
  [56.3, 39.1, 77.6],
  [58.1, 41.4, 79.5],
  [59.8, 42.6, 81.4],
  [61.6, 43.9, 83.3],
  [63.3, 46.4, 85.3],
  [65.1, 47.7, 87.3],
  [66.9, 49.0, 89.5],
  [68.7, 50.3, 91.6],
  [70.5, 51.7, 93.9],
  [72.4, 53.0, 94.6],
  [74.2, 55.8, 96.2],
  [76.1, 57.2, 98.5],
  [78.0, 58.6, 101.0],
  [79.9, 60.1, 103.6],
  [81.8, 61.5, 105.7],
  [83.7, 63.0, 107.0],
  [85.7, 66.1, 109.1],
  [87.6, 67.6, 111.8],
  [89.6, 69.1, 113.8],
  [91.6, 70.7, 115.4],
  [93.6, 72.2, 118.0],
  [95.6, 73.8, 120.4],
  [97.7, 75.4, 121.9],
  [99.7, 78.9, 124.7],
  [101.8, 80.5, 127.1],
  [103.9, 82.2, 128.9],
  [106.0, 83.9, 131.5],
  [108.2, 85.6, 133.7],
  [110.3, 87.3, 135.3],
  [112.5, 89.0, 138.7],
  [114.7, 93.0, 140.4],
  [116.9, 94.7, 142.7],
  [119.1, 96.5, 145.4],
  [121.4, 98.4, 147.9],
  [123.6, 100.2, 150.0],
  [125.9, 102.1, 152.3],
  [128.2, 103.9, 154.7],
  [130.5, 105.8, 156.6],
  [132.9, 110.3, 159.8],
  [135.3, 112.3, 162.4],
  [137.7, 114.3, 163.9],
  [140.1, 116.3, 167.8],
  [142.5, 118.3, 168.8],
  [48.7, 32.8, 69.0],
  [50.4, 33.9, 70.9],
  [52.1, 36.1, 73.0],
  [53.8, 37.3, 75.0],
  [55.6, 38.5, 77.2],
  [57.3, 39.7, 79.1],
  [59.1, 42.1, 81.2],
  [60.9, 43.4, 83.3],
  [62.7, 44.7, 85.3],
  [64.5, 46.0, 87.3],
  [66.3, 48.6, 89.5],
  [68.2, 49.9, 91.6],
  [70.0, 51.3, 93.9],
  [71.9, 52.6, 94.6],
  [73.8, 54.0, 96.2],
  [75.7, 56.9, 98.5],
  [77.6, 58.4, 101.0],
  [79.5, 59.8, 103.6],
  [81.5, 61.3, 106.2],
  [83.5, 62.8, 108.0],
  [85.4, 64.3, 109.4],
  [87.5, 65.8, 111.8],
  [89.5, 69.0, 114.7],
  [91.5, 70.6, 116.4],
  [93.6, 72.2, 118.3],
  [95.7, 73.8, 121.2],
  [97.8, 75.4, 123.1],
  [99.9, 77.0, 125.1],
  [102.0, 78.7, 127.6],
  [104.2, 82.4, 130.5],
  [106.3, 84.1, 132.1],
  [108.5, 85.8, 135.0],
  [110.7, 87.6, 137.0],
  [113.0, 89.4, 139.5],
  [115.2, 91.1, 142.1],
  [117.5, 95.2, 144.2],
  [119.8, 94.8, 146.2],
  [122.1, 99.0, 148.9],
  [124.5, 100.9, 151.7],
  [126.8, 102.8, 154.0],
  [129.2, 104.7, 156.2],
  [131.6, 106.7, 159.5],
  [134.0, 111.3, 161.3],
  [136.5, 113.3, 163.7],
  [139.0, 115.4, 167.2],
  [141.5, 117.4, 168.3],
  [144.0, 119.5, 172.4],
  [146.6, 121.7, 174.4],
  [149.1, 123.8, 177.0],
  [51.2, 34.5, 72.3],
  [53.0, 36.7, 74.3],
  [54.8, 38.0, 76.4],
  [56.5, 39.2, 78.6],
  [58.3, 40.5, 80.6],
  [60.2, 42.9, 83.0],
  [62.0, 44.2, 85.1],
  [63.8, 45.5, 87.2],
  [65.7, 46.8, 89.2],
  [67.6, 49.5, 91.6],
  [69.5, 50.9, 93.9],
  [71.4, 52.3, 94.6],
  [73.3, 53.7, 96.2],
  [75.2, 55.1, 98.5],
  [77.2, 58.1, 101.0],
  [79.2, 59.5, 103.6],
  [81.2, 61.0, 106.2],
  [83.2, 62.6, 108.3],
  [85.2, 64.1, 110.3],
  [87.3, 65.6, 112.2],
  [89.3, 67.2, 114.9],
  [91.4, 70.5, 117.5],
  [93.6, 72.2, 119.2],
  [95.7, 73.8, 121.8],
  [97.8, 75.5, 124.2],
  [100.0, 77.2, 126.0],
  [102.2, 78.8, 128.9],
  [104.4, 80.6, 130.6],
  [106.6, 84.4, 133.7],
  [108.9, 86.1, 135.3],
  [111.2, 88.0, 138.7],
  [113.5, 89.8, 140.5],
  [115.8, 91.6, 143.0],
  [118.2, 95.8, 145.9],
  [120.5, 95.3, 148.2],
  [122.9, 99.6, 150.9],
  [125.4, 101.6, 153.0],
  [127.8, 103.6, 155.9],
  [130.3, 105.6, 158.7],
  [132.8, 107.6, 160.7],
  [135.3, 109.7, 163.7],
  [137.8, 111.7, 167.2],
  [140.4, 113.8, 168.3],
  [143.0, 118.7, 172.4],
  [145.6, 120.9, 174.4],
  [148.3, 123.1, 177.0],
  [150.9, 125.3, 180.8],
  [153.7, 127.5, 182.1],
  [156.4, 129.8, 186.4],
  [53.9, 37.4, 75.8],
  [55.7, 38.6, 77.9],
  [57.6, 39.9, 80.0],
  [59.4, 41.2, 82.2],
  [61.3, 43.7, 84.6],
  [63.1, 45.0, 86.9],
  [65.0, 46.4, 89.2],
  [67.0, 47.7, 91.5],
  [68.9, 50.5, 93.8],
  [70.8, 50.5, 94.6],
  [72.8, 51.9, 96.2],
  [74.8, 54.8, 98.5],
  [76.8, 56.2, 101.0],
  [78.8, 57.7, 103.6],
  [80.8, 60.8, 106.2],
  [82.9, 62.3, 108.8],
  [85.0, 63.9, 111.1],
  [87.1, 65.5, 112.8],
  [89.2, 67.1, 115.1],
  [91.4, 68.7, 118.0],
  [93.5, 70.3, 120.4],
  [95.7, 73.8, 121.9],
  [97.9, 75.5, 125.0],
  [100.2, 77.3, 127.3],
  [102.4, 79.0, 129.4],
  [104.7, 80.8, 132.1],
  [107.0, 84.6, 135.0],
  [109.3, 84.3, 137.0],
  [111.7, 86.1, 139.5],
  [114.0, 90.2, 142.2],
  [116.4, 92.1, 145.0],
  [118.9, 94.0, 147.9],
  [121.3, 96.0, 150.0],
  [123.8, 97.9, 152.6],
  [126.3, 102.4, 155.5],
  [128.8, 101.9, 158.2],
  [131.4, 106.5, 160.1],
  [134.0, 108.6, 163.7],
  [136.6, 110.7, 166.5],
  [139.2, 112.9, 168.0],
  [141.9, 115.0, 172.4],
  [144.6, 117.2, 174.4],
  [147.4, 119.4, 177.0],
  [150.1, 124.6, 180.8],
  [152.9, 126.9, 182.1],
  [155.7, 129.3, 187.0],
  [158.6, 131.6, 188.5],
  [161.5, 134.0, 192.5],
  [164.4, 136.5, 194.9],
  [56.8, 39.4, 79.5],
  [58.6, 40.7, 81.5],
  [60.5, 42.0, 83.7],
  [62.4, 43.3, 86.2],
  [64.4, 44.6, 88.6],
  [66.3, 47.3, 90.4],
  [68.3, 48.7, 93.0],
  [70.3, 50.1, 94.6],
  [72.3, 51.5, 96.4],
  [74.3, 53.0, 98.8],
  [76.3, 55.9, 101.2],
  [78.4, 57.4, 103.6],
  [80.5, 59.0, 106.2],
  [82.6, 60.5, 109.0],
  [84.7, 62.1, 111.4],
  [86.9, 65.3, 113.8],
  [89.1, 67.0, 115.4],
  [91.3, 68.6, 118.3],
  [93.5, 70.3, 121.2],
  [95.7, 72.0, 123.1],
  [98.0, 73.7, 126.0],
  [100.3, 75.4, 128.2],
  [102.6, 79.2, 130.6],
  [105.0, 81.0, 133.7],
  [107.3, 82.8, 135.3],
  [109.7, 84.7, 138.7],
  [112.2, 86.5, 140.7],
  [114.6, 88.4, 144.0],
  [117.1, 92.6, 146.2],
  [119.6, 94.6, 148.9],
  [122.2, 96.6, 152.0],
  [124.7, 98.7, 154.7],
  [127.3, 100.7, 157.0],
  [129.9, 102.8, 159.9],
  [132.6, 107.5, 163.6],
  [135.3, 109.7, 165.7],
  [138.0, 111.9, 168.0],
  [140.8, 114.1, 172.4],
  [143.6, 116.4, 174.4],
  [146.4, 118.6, 177.0],
  [149.2, 121.0, 181.4],
  [152.1, 123.3, 182.9],
  [155.0, 125.7, 187.3],
  [158.0, 128.1, 190.0],
  [161.0, 130.5, 192.8],
  [164.0, 136.1, 195.8],
  [167.1, 138.7, 198.9],
  [170.2, 141.3, 202.9],
  [173.3, 143.9, 205.9],
  [59.8, 41.4, 83.4],
  [61.7, 42.8, 85.4],
  [63.7, 44.1, 87.8],
  [65.7, 45.5, 90.3],
  [67.7, 46.9, 92.7],
  [69.7, 49.7, 95.3],
  [71.7, 51.1, 97.5],
  [73.8, 52.6, 99.5],
  [75.9, 54.1, 101.4],
  [78.0, 55.6, 103.8],
  [80.1, 58.7, 106.4],
  [82.3, 60.3, 109.1],
  [84.5, 61.9, 111.8],
  [86.7, 63.5, 114.7],
  [88.9, 66.9, 116.4],
  [91.2, 68.6, 119.2],
  [93.5, 70.3, 121.8],
  [95.8, 72.0, 124.2],
  [98.1, 73.8, 127.1],
  [100.5, 75.5, 129.1],
  [102.9, 77.3, 132.1],
  [105.3, 81.2, 135.0],
  [107.7, 83.1, 137.0],
  [110.2, 85.0, 140.4],
  [112.7, 87.0, 142.7],
  [115.2, 88.9, 145.4],
  [117.8, 93.2, 148.2],
  [120.4, 95.2, 151.7],
  [123.0, 94.9, 154.0],
  [125.7, 99.4, 156.6],
  [128.4, 101.6, 159.8],
  [131.1, 103.7, 163.1],
  [133.9, 105.9, 165.2],
  [136.7, 110.8, 168.0],
  [139.5, 110.4, 172.4],
  [142.4, 115.4, 174.4],
  [145.3, 117.8, 177.0],
  [148.3, 120.2, 181.9],
  [151.3, 122.6, 183.1],
  [154.3, 125.1, 187.3],
  [157.3, 127.5, 190.0],
  [160.5, 130.1, 193.1],
  [163.6, 135.8, 197.7],
  [166.8, 138.4, 200.4],
  [170.0, 141.1, 203.5],
  [173.3, 143.8, 206.2],
  [176.6, 146.6, 210.2],
  [179.9, 149.4, 213.8],
  [183.3, 152.2, 217.5],
  [62.9, 43.6, 87.4],
  [65.0, 45.0, 89.6],
  [67.0, 46.5, 92.0],
  [69.1, 47.9, 95.6],
  [71.2, 50.7, 98.3],
  [73.3, 52.2, 100.1],
  [75.4, 53.8, 102.6],
  [77.6, 55.3, 104.5],
  [79.8, 56.9, 107.0],
  [82.0, 60.0, 109.4],
  [84.2, 61.7, 112.2],
  [86.5, 63.3, 114.9],
  [88.8, 65.0, 117.5],
  [91.1, 66.7, 120.2],
  [93.4, 70.3, 121.9],
  [95.8, 72.0, 125.0],
  [98.2, 73.8, 127.6],
  [100.6, 75.7, 130.6],
  [103.1, 77.5, 133.7],
  [105.6, 79.4, 135.3],
  [108.1, 83.4, 138.7],
  [110.7, 83.2, 142.1],
  [113.3, 87.4, 144.2],
  [115.9, 89.4, 147.9],
  [118.6, 91.5, 150.0],
  [121.3, 93.6, 152.6],
  [124.0, 95.7, 155.9],
  [126.8, 97.8, 159.5],
  [129.6, 102.5, 162.7],
  [132.4, 104.8, 164.6],
  [135.3, 107.0, 168.0],
  [138.2, 109.3, 172.4],
  [141.2, 111.7, 174.4],
  [144.2, 114.1, 177.0],
  [147.3, 116.5, 181.9],
  [150.3, 118.9, 184.2],
  [153.5, 124.4, 187.3],
  [156.7, 127.0, 191.9],
  [159.9, 129.6, 194.0],
  [163.1, 132.2, 198.0],
  [166.5, 134.9, 200.4],
  [169.8, 137.6, 205.3],
  [173.2, 140.4, 209.3],
  [176.7, 146.7, 212.3],
  [180.2, 149.6, 215.7],
  [183.7, 152.5, 219.3],
  [187.3, 155.5, 222.7],
  [191.0, 158.5, 227.0],
  [194.7, 161.6, 229.7],
  [66.3, 46.0, 91.7],
  [68.4, 47.5, 96.2],
  [70.6, 48.9, 98.5],
  [72.7, 51.9, 101.0],
  [74.9, 53.4, 102.9],
  [77.1, 55.0, 105.7],
  [79.4, 56.6, 107.7],
  [81.6, 58.2, 110.3],
  [83.9, 59.8, 112.8],
  [86.2, 63.2, 115.4],
  [88.6, 64.9, 118.3],
  [91.0, 66.6, 121.0],
  [93.4, 68.4, 123.1],
  [95.8, 70.2, 126.0],
  [98.3, 73.9, 128.9],
  [100.8, 75.8, 132.1],
  [103.4, 77.7, 135.0],
  [105.9, 79.7, 137.0],
  [108.6, 81.6, 140.4],
  [111.2, 83.6, 143.0],
  [113.9, 85.6, 146.2],
  [116.6, 90.0, 148.9],
  [119.4, 92.1, 152.3],
  [122.2, 94.3, 155.5],
  [125.0, 96.5, 158.7],
  [127.9, 98.7, 161.3],
  [130.8, 103.5, 163.9],
  [133.8, 105.9, 168.0],
  [136.8, 105.6, 172.4],
  [139.9, 110.7, 174.4],
  [143.0, 113.1, 177.0],
  [146.2, 115.6, 181.9],
  [149.4, 118.1, 185.4],
  [152.6, 120.7, 187.3],
  [155.9, 123.3, 192.2],
  [159.2, 126.0, 194.9],
  [162.6, 131.8, 198.9],
  [166.1, 134.6, 203.5],
  [169.6, 137.5, 206.2],
  [173.2, 140.4, 209.3],
  [176.8, 143.3, 213.8],
  [180.4, 146.3, 218.1],
  [184.2, 149.3, 221.8],
  [188.0, 152.3, 226.4],
  [191.8, 159.2, 229.7],
  [195.7, 162.4, 234.0],
  [199.7, 165.7, 238.0],
  [203.7, 169.1, 243.1],
  [207.7, 172.4, 245.9],
  [70.0, 48.5, 98.5],
  [72.2, 50.0, 101.1],
  [74.4, 53.0, 103.6],
  [76.7, 54.6, 106.2],
  [78.9, 56.3, 108.8],
  [81.3, 57.9, 111.4],
  [83.6, 59.6, 113.8],
  [86.0, 61.3, 115.5],
  [88.4, 63.0, 119.2],
  [90.9, 66.6, 121.6],
  [93.4, 68.4, 124.2],
  [95.9, 70.2, 127.6],
  [98.4, 72.1, 130.6],
  [101.0, 74.0, 133.7],
  [103.6, 75.9, 135.3],
  [106.3, 77.9, 138.7],
  [109.0, 82.0, 142.2],
  [111.8, 84.0, 145.4],
  [114.6, 86.1, 148.2],
  [117.4, 88.3, 151.7],
  [120.3, 92.8, 154.7],
  [123.2, 95.0, 158.2],
  [126.1, 94.9, 160.7],
  [129.2, 99.6, 163.7],
  [132.2, 102.0, 168.0],
  [135.3, 104.4, 172.4],
  [138.5, 106.8, 174.4],
  [141.7, 109.3, 177.0],
  [145.0, 114.7, 181.9],
  [148.3, 117.3, 185.5],
  [151.7, 120.0, 187.8],
  [155.1, 122.7, 192.8],
  [158.6, 125.4, 196.8],
  [162.1, 128.2, 200.4],
  [165.7, 131.1, 203.8],
  [169.4, 134.0, 208.0],
  [173.1, 140.3, 212.3],
  [176.9, 143.4, 215.7],
  [180.7, 146.5, 219.3],
  [184.7, 149.7, 223.6],
  [188.7, 152.9, 228.5],
  [192.7, 156.2, 233.7],
  [196.8, 159.5, 236.1],
  [201.0, 166.9, 242.5],
  [205.3, 170.4, 245.0],
  [209.6, 174.0, 251.4],
  [214.0, 177.6, 254.1],
  [218.5, 181.3, 260.5],
  [223.0, 185.1, 264.5],
  [73.8, 51.2, 103.6],
  [76.2, 52.8, 106.2],
  [78.5, 56.0, 109.0],
  [80.9, 57.7, 111.7],
  [83.3, 59.4, 114.6],
  [85.7, 61.1, 117.2],
  [88.2, 62.9, 120.2],
  [90.8, 66.5, 123.1],
  [93.3, 66.5, 125.4],
  [95.9, 70.2, 128.9],
  [98.5, 72.2, 132.1],
  [101.2, 74.1, 135.0],
  [103.9, 76.1, 137.0],
  [106.7, 78.2, 140.4],
  [109.5, 80.2, 144.0],
  [112.4, 84.5, 147.9],
  [115.3, 86.7, 150.0],
  [118.2, 88.9, 154.0],
  [121.2, 91.1, 156.6],
  [124.3, 95.9, 159.9],
  [127.4, 95.8, 163.7],
  [130.5, 98.1, 168.0],
  [133.7, 103.2, 172.4],
  [137.0, 105.7, 174.4],
  [140.3, 108.2, 177.0],
  [143.7, 110.8, 181.9],
  [147.1, 113.5, 186.9],
  [150.6, 119.2, 190.0],
  [154.2, 119.0, 193.1],
  [157.8, 124.8, 197.7],
  [161.5, 127.8, 200.4],
  [165.3, 130.8, 205.9],
  [169.1, 133.8, 209.3],
  [173.0, 136.9, 213.8],
  [177.0, 140.0, 219.3],
  [181.1, 143.2, 222.4],
  [185.2, 146.5, 227.0],
  [189.4, 153.5, 230.9],
  [193.7, 157.0, 235.5],
  [198.1, 160.6, 242.2],
  [202.5, 164.2, 244.1],
  [207.1, 167.8, 251.4],
  [211.7, 175.7, 254.1],
  [216.4, 175.4, 260.5],
  [221.1, 183.6, 264.5],
  [226.0, 187.6, 270.3],
  [231.0, 191.7, 274.9],
  [236.0, 195.9, 281.0],
  [241.1, 200.2, 285.6],
  [78.0, 54.1, 109.0],
  [80.5, 55.8, 111.8],
  [83.0, 57.5, 114.9],
  [85.5, 60.9, 118.0],
  [88.0, 62.8, 121.0],
  [90.6, 64.6, 124.1],
  [93.3, 66.5, 126.3],
  [95.9, 68.4, 130.5],
  [98.7, 72.3, 133.7],
  [101.4, 74.3, 136.1],
  [104.3, 76.4, 139.3],
  [107.1, 78.5, 142.7],
  [110.0, 80.6, 146.2],
  [113.0, 82.8, 149.1],
  [116.0, 87.2, 152.6],
  [119.1, 89.6, 156.1],
  [122.2, 91.9, 159.8],
  [125.4, 94.3, 163.7],
  [128.7, 94.2, 168.0],
  [132.0, 99.2, 172.4],
  [135.4, 101.8, 174.4],
  [138.8, 107.1, 177.0],
  [142.3, 107.0, 181.9],
  [145.9, 109.7, 187.2],
  [149.5, 115.3, 190.1],
  [153.2, 118.2, 194.0],
  [157.0, 121.1, 198.9],
  [160.9, 124.1, 203.5],
  [164.8, 130.4, 206.2],
  [168.9, 133.6, 212.3],
  [173.0, 133.4, 215.7],
  [177.2, 140.1, 221.8],
  [181.5, 143.5, 226.4],
  [185.8, 147.0, 229.7],
  [190.3, 150.5, 235.5],
  [194.8, 154.1, 240.7],
  [199.5, 161.7, 243.4],
  [204.2, 165.5, 251.4],
  [209.1, 169.5, 254.1],
  [214.0, 173.5, 260.5],
  [219.1, 177.6, 266.0],
  [224.2, 186.1, 270.3],
  [229.4, 186.0, 277.9],
  [234.8, 194.9, 282.2],
  [240.2, 194.7, 290.1],
  [245.8, 204.0, 294.4],
  [251.5, 208.7, 302.0],
  [257.2, 213.5, 306.9],
  [263.1, 218.4, 314.5],
  [82.6, 57.3, 115.4],
  [85.2, 59.1, 118.3],
  [87.8, 60.9, 121.9],
  [90.5, 62.8, 125.0],
  [93.2, 66.5, 128.0],
  [96.0, 68.4, 131.7],
  [98.8, 70.4, 135.3],
  [101.7, 72.5, 138.2],
  [104.6, 74.6, 142.1],
  [107.6, 78.8, 145.4],
  [110.6, 81.0, 148.8],
  [113.7, 83.3, 151.7],
  [116.9, 85.6, 155.9],
  [120.1, 87.9, 159.8],
  [123.4, 92.8, 163.7],
  [126.7, 95.3, 168.0],
  [130.1, 95.3, 172.4],
  [133.6, 97.8, 174.4],
  [137.2, 103.1, 177.0],
  [140.8, 105.9, 182.0],
  [144.5, 108.7, 187.2],
  [148.3, 111.5, 192.3],
  [152.2, 114.4, 195.0],
  [156.1, 120.5, 200.4],
  [160.2, 123.6, 205.3],
  [164.3, 126.8, 209.3],
  [168.6, 130.0, 213.8],
  [172.9, 133.4, 219.3],
  [177.3, 136.8, 222.7],
  [181.9, 140.3, 228.5],
  [186.5, 147.5, 234.0],
  [191.3, 151.3, 238.0],
  [196.1, 155.1, 243.1],
  [201.1, 159.1, 251.4],
  [206.2, 163.1, 254.1],
  [211.4, 167.2, 260.5],
  [216.7, 175.7, 268.2],
  [222.2, 175.7, 270.9],
  [227.7, 184.6, 279.8],
  [233.4, 189.2, 283.1],
  [239.2, 193.9, 290.4],
  [245.2, 198.7, 296.2],
  [251.3, 208.6, 303.3],
  [257.5, 208.7, 310.9],
  [263.8, 219.0, 315.5],
  [270.3, 219.1, 325.8],
  [276.9, 229.8, 328.6],
  [283.6, 235.4, 340.8],
  [290.5, 241.2, 343.5],
  [87.6, 60.7, 123.1],
  [90.4, 62.7, 126.0],
  [93.2, 64.6, 129.1],
  [96.0, 68.5, 132.1],
  [99.0, 70.5, 136.7],
  [101.9, 72.7, 140.4],
  [105.0, 74.8, 143.9],
  [108.1, 77.0, 147.2],
  [111.2, 79.3, 151.7],
  [114.5, 81.6, 155.5],
  [117.8, 86.3, 158.2],
  [121.1, 88.7, 163.3],
  [124.6, 91.2, 167.8],
  [128.1, 93.8, 172.1],
  [131.7, 96.5, 174.4],
  [135.4, 99.2, 177.0],
  [139.1, 101.9, 182.0],
  [143.0, 107.5, 187.2],
  [147.0, 110.5, 192.9],
  [151.0, 113.5, 197.7],
  [155.2, 116.7, 200.4],
  [159.4, 119.9, 206.2],
  [163.8, 123.1, 212.3],
  [168.2, 126.5, 215.7],
  [172.8, 133.3, 221.8],
  [177.5, 133.5, 227.0],
  [182.3, 140.7, 233.7],
  [187.3, 144.5, 238.0],
  [192.4, 148.4, 243.1],
  [197.6, 152.4, 251.4],
  [202.9, 160.5, 254.1],
  [208.4, 160.8, 260.5],
  [214.0, 169.3, 269.7],
  [219.8, 173.9, 273.3],
  [225.8, 178.6, 281.0],
  [231.8, 187.9, 287.7],
  [238.1, 188.3, 294.4],
  [244.5, 198.2, 302.0],
  [251.0, 198.6, 306.9],
  [257.7, 208.9, 315.5],
  [264.6, 214.5, 322.8],
  [271.7, 220.2, 328.6],
  [278.9, 226.0, 340.8],
  [286.3, 232.0, 343.5],
  [293.8, 238.1, 356.3],
  [301.5, 244.4, 360.0],
  [309.4, 256.8, 372.4],
  [317.4, 263.5, 378.5],
  [325.7, 270.3, 388.2],
  [93.1, 64.6, 130.6],
  [96.1, 66.6, 135.3],
  [99.1, 68.7, 138.7],
  [102.2, 70.9, 142.2],
  [105.4, 75.1, 145.4],
  [108.6, 77.4, 150.0],
  [111.9, 79.8, 154.0],
  [115.3, 82.2, 158.1],
  [118.7, 84.7, 162.7],
  [122.3, 87.2, 167.2],
  [125.9, 92.2, 172.0],
  [129.6, 95.0, 174.4],
  [133.4, 95.1, 177.9],
  [137.4, 97.9, 182.9],
  [141.4, 103.5, 187.8],
  [145.5, 106.6, 193.2],
  [149.7, 109.7, 199.0],
  [154.1, 115.9, 203.5],
  [158.5, 119.2, 209.3],
  [163.1, 122.7, 213.8],
  [167.9, 126.2, 219.3],
  [172.7, 129.9, 226.4],
  [177.7, 133.6, 229.7],
  [182.9, 137.5, 235.5],
  [188.2, 141.5, 243.1],
  [193.6, 145.6, 251.4],
  [199.3, 153.7, 254.1],
  [205.1, 158.2, 260.5],
  [211.0, 162.8, 270.3],
  [217.2, 167.5, 274.9],
  [223.5, 172.4, 282.2],
  [230.0, 177.5, 290.4],
  [236.7, 187.3, 296.2],
  [243.6, 188.0, 305.1],
  [250.8, 198.4, 314.5],
  [258.1, 204.1, 320.9],
  [265.6, 210.1, 328.6],
  [273.3, 221.5, 340.8],
  [281.2, 222.5, 343.5],
  [289.4, 234.6, 359.4],
  [297.8, 235.5, 364.3],
  [306.3, 248.3, 378.5],
  [315.1, 255.4, 382.7],
  [324.1, 262.7, 398.6],
  [333.3, 270.2, 401.7],
  [342.8, 277.8, 418.7],
  [352.4, 285.7, 423.0],
  [362.3, 293.7, 439.5],
  [372.4, 301.8, 446.2],
  [99.3, 68.8, 140.4],
  [102.5, 71.1, 144.2],
  [105.8, 73.4, 148.9],
  [109.2, 75.7, 152.6],
  [112.6, 78.1, 156.6],
  [116.2, 80.6, 160.7],
  [119.8, 83.1, 165.2],
  [123.6, 85.7, 170.1],
  [127.4, 90.8, 176.4],
  [131.4, 93.6, 180.4],
  [135.4, 96.5, 184.0],
  [139.6, 99.5, 190.0],
  [143.9, 102.6, 195.0],
  [148.3, 105.7, 199.9],
  [152.9, 112.0, 206.2],
  [157.6, 115.4, 212.3],
  [162.4, 119.0, 215.7],
  [167.4, 122.6, 222.7],
  [172.6, 126.4, 228.5],
  [178.0, 130.3, 235.5],
  [183.5, 134.4, 243.1],
  [189.2, 142.3, 251.4],
  [195.1, 146.7, 254.1],
  [201.2, 151.3, 260.5],
  [207.5, 156.1, 270.3],
  [214.1, 161.0, 279.8],
  [220.9, 170.4, 283.1],
  [227.9, 175.8, 294.4],
  [235.2, 176.8, 302.0],
  [242.7, 187.2, 310.9],
  [250.4, 193.2, 318.2],
  [258.4, 199.4, 328.6],
  [266.7, 205.8, 340.8],
  [275.3, 212.4, 343.5],
  [284.1, 219.2, 360.0],
  [293.3, 232.0, 365.5],
  [302.6, 233.5, 378.3],
  [312.3, 247.0, 388.2],
  [322.3, 254.9, 401.7],
  [332.5, 263.0, 413.9],
  [343.0, 278.0, 423.0],
  [353.8, 279.9, 439.5],
  [364.9, 295.7, 446.2],
  [376.2, 297.6, 467.0],
  [387.9, 314.4, 472.5],
  [399.8, 316.2, 495.6],
  [412.0, 334.0, 498.7],
  [424.5, 344.1, 524.9],
  [437.4, 354.5, 526.2],
  [106.3, 71.6, 151.7],
  [109.8, 76.1, 155.9],
  [113.4, 78.7, 159.9],
  [117.2, 81.3, 163.9],
  [121.0, 83.9, 168.3],
  [125.0, 86.7, 176.8],
  [129.1, 89.5, 181.9],
  [133.3, 92.4, 186.9],
  [137.6, 98.1, 192.3],
  [142.1, 101.3, 196.8],
  [146.7, 104.6, 201.1],
  [151.5, 108.0, 207.8],
  [156.5, 111.6, 215.4],
  [161.6, 115.2, 220.6],
  [167.0, 119.0, 227.9],
  [172.5, 123.0, 235.5],
  [178.2, 130.5, 243.1],
  [184.2, 134.9, 251.4],
  [190.4, 135.7, 254.1],
  [196.8, 144.2, 260.8],
  [203.5, 149.1, 270.3],
  [210.5, 154.2, 281.0],
  [217.8, 159.5, 290.4],
  [225.4, 169.5, 296.2],
  [233.3, 175.4, 306.9],
  [241.5, 176.9, 315.5],
  [250.0, 188.0, 328.6],
  [258.9, 194.7, 340.8],
  [268.2, 201.6, 343.5],
  [277.8, 208.9, 360.0],
  [287.8, 216.4, 372.2],
  [298.1, 230.0, 382.9],
  [308.8, 232.2, 398.6],
  [319.9, 246.8, 405.3],
  [331.4, 255.7, 423.0],
  [343.3, 264.8, 439.5],
  [355.5, 281.2, 446.2],
  [368.1, 284.0, 471.2],
  [381.1, 294.0, 476.1],
  [394.5, 312.1, 498.1],
  [408.3, 315.0, 515.2],
  [422.5, 334.2, 526.2],
  [437.1, 337.2, 555.5],
  [452.0, 357.6, 556.7],
  [467.4, 369.8, 586.6],
  [483.3, 382.3, 600.6],
  [499.6, 404.9, 618.9],
  [516.3, 408.4, 648.2],
  [533.5, 422.0, 652.5],
  [114.3, 79.3, 163.7],
  [118.3, 82.0, 168.0],
  [122.4, 84.8, 177.0],
  [126.6, 85.3, 181.9],
  [130.9, 88.2, 187.2],
  [135.4, 91.3, 192.7],
  [140.1, 97.2, 198.8],
  [145.0, 100.5, 204.8],
  [150.0, 104.0, 211.8],
  [155.3, 107.7, 218.7],
  [160.7, 111.4, 226.4],
  [166.4, 115.4, 234.0],
  [172.3, 119.5, 242.2],
  [178.5, 123.8, 250.5],
  [185.0, 131.9, 256.3],
  [191.8, 136.7, 264.5],
  [198.9, 141.8, 273.3],
  [206.4, 147.1, 283.1],
  [214.2, 152.7, 294.4],
  [222.4, 158.5, 303.3],
  [231.0, 169.2, 315.5],
  [240.0, 175.8, 328.6],
  [249.5, 177.9, 340.8],
  [259.5, 185.0, 343.5],
  [270.0, 197.7, 360.0],
  [280.9, 205.8, 378.3],
  [292.4, 214.2, 388.4],
  [304.4, 217.0, 401.8],
  [316.9, 232.1, 423.0],
  [330.0, 248.2, 439.5],
  [343.6, 251.7, 446.2],
  [357.8, 262.0, 471.2],
  [372.5, 280.1, 495.6],
  [387.7, 299.1, 498.7],
  [403.4, 295.5, 526.2],
  [419.8, 315.6, 555.5],
  [436.6, 336.9, 556.7],
  [454.1, 341.4, 586.6],
  [472.1, 355.0, 618.9],
  [490.7, 378.6, 623.8],
  [509.9, 393.4, 652.5],
  [529.8, 398.4, 687.9],
  [550.4, 424.6, 700.1],
  [571.7, 441.1, 724.5],
  [593.8, 458.1, 763.6],
  [616.7, 487.8, 786.4],
  [640.5, 494.2, 804.8],
  [665.3, 513.2, 847.5],
  [691.0, 546.6, 886.5],
  [123.9, 83.5, 181.9],
  [128.4, 86.5, 187.2],
  [133.1, 89.7, 192.9],
  [137.9, 92.9, 199.0],
  [143.0, 99.2, 206.3],
  [148.3, 102.8, 212.4],
  [153.9, 106.7, 222.4],
  [159.7, 110.7, 229.7],
  [165.8, 114.9, 238.0],
  [172.2, 119.4, 245.0],
  [178.9, 124.0, 257.8],
  [186.0, 125.3, 268.8],
  [193.5, 130.4, 279.5],
  [201.4, 135.7, 284.0],
  [209.8, 145.5, 301.1],
  [218.7, 151.7, 314.5],
  [228.2, 158.2, 323.1],
  [238.2, 165.2, 340.8],
  [248.9, 172.6, 350.2],
  [260.3, 175.4, 365.2],
  [272.3, 183.5, 382.9],
  [285.1, 197.7, 398.8],
  [298.7, 207.1, 423.2],
  [313.0, 217.0, 439.5],
  [328.2, 233.9, 448.0],
  [344.1, 245.3, 472.5],
  [360.9, 257.3, 498.7],
  [378.4, 262.4, 526.2],
  [396.8, 275.1, 555.5],
  [416.0, 296.6, 556.7],
  [436.0, 310.9, 586.6],
  [456.9, 334.6, 618.9],
  [478.6, 341.2, 652.5],
  [501.2, 357.3, 687.9],
  [524.7, 374.1, 700.1],
  [549.3, 402.3, 724.5],
  [574.8, 432.2, 763.6],
  [601.5, 440.6, 804.5],
  [629.4, 448.7, 847.5],
  [658.6, 482.4, 886.5],
  [689.3, 518.3, 892.6],
  [721.5, 542.6, 941.5],
  [755.6, 553.4, 993.9],
  [791.5, 579.7, 1048.9],
  [829.7, 623.9, 1108.7],
  [870.4, 654.5, 1153.8],
  [913.9, 705.0, 1174.6],
  [960.6, 703.6, 1245.4],
  [1011.2, 740.6, 1323.5],
  [135.5, 88.7, 203.4],
  [140.8, 92.1, 209.3],
  [146.4, 98.7, 219.3],
  [152.3, 102.6, 228.4],
  [158.5, 106.8, 235.5],
  [165.0, 111.2, 243.4],
  [172.0, 115.9, 260.5],
  [179.3, 120.8, 270.3],
  [187.2, 126.1, 281.0],
  [195.6, 128.0, 292.9],
  [204.6, 137.9, 306.9],
  [214.3, 140.2, 320.9],
  [224.7, 147.0, 343.5],
  [235.9, 159.0, 360.0],
  [248.1, 162.3, 371.9],
  [261.3, 170.9, 398.5],
  [275.5, 185.7, 416.8],
  [290.9, 190.4, 446.1],
  [307.6, 195.3, 471.2],
  [325.5, 206.6, 498.1],
  [344.8, 218.9, 520.7],
  [365.4, 231.9, 555.5],
  [387.3, 245.9, 567.0],
  [410.6, 260.6, 618.9],
  [435.2, 276.2, 650.0],
  [461.1, 292.7, 687.9],
  [488.4, 310.0, 721.5],
  [517.2, 338.4, 763.6],
  [547.5, 358.2, 804.5],
  [579.4, 379.1, 847.2],
  [613.1, 401.2, 879.2],
  [648.8, 424.5, 941.5],
  [686.7, 449.3, 974.4],
  [727.0, 475.7, 1048.9],
  [770.1, 549.0, 1094.0],
  [816.4, 550.1, 1174.6],
  [866.4, 583.8, 1245.4],
  [920.8, 620.5, 1282.0],
  [980.4, 660.6, 1410.2],
  [1046.2, 705.0, 1509.0],
  [1119.9, 754.6, 1614.0],
  [1203.3, 810.8, 1750.7],
  [1299.7, 850.4, 1896.6],
  [1413.6, 924.9, 2101.6],
  [1553.1, 1016.2, 2353.1],
  [1732.9, 1167.7, 2709.5],
  [1986.3, 1222.0, 3300.2],
  [2419.6, 1630.4, 4716.1],
  ['>2419.6', 1439.5, 'infinite'],
];

/**
 * MPN lookup table for Legionella testing
 * Specialized lookup table for Legionella bacteria analysis
 * Contains flattened data for position-based lookups
 */
const mpnLegio: Array<string | number> = [
  '<1',
  1.1,
  2.3,
  3.9,
  5.8,
  8.4,
  12.6,
  1.0,
  2.2,
  3.5,
  5.2,
  7.4,
  10.4,
  15.5,
  2.0,
  3.2,
  4.7,
  6.6,
  9.0,
  12.4,
  18.7,
  3.0,
  4.3,
  5.9,
  7.9,
  10.6,
  14.6,
  22.3,
  4.0,
  5.4,
  7.2,
  9.4,
  12.3,
  16.9,
  26.4,
  5.0,
  6.5,
  8.4,
  10.8,
  14.1,
  19.4,
  31.0,
  6.0,
  7.7,
  9.7,
  12.3,
  15.9,
  21.9,
  36.1,
  7.0,
  8.8,
  10.9,
  13.8,
  17.8,
  24.6,
  41.6,
  8.1,
  9.9,
  12.2,
  15.3,
  19.8,
  27.5,
  47.4,
  9.1,
  11.0,
  13.5,
  16.8,
  21.7,
  30.5,
  53.4,
  10.1,
  12.2,
  14.8,
  18.4,
  23.8,
  33.5,
  59.6,
  11.1,
  13.3,
  16.1,
  20.0,
  25.9,
  36.7,
  65.9,
  12.1,
  14.5,
  17.5,
  21.6,
  28.0,
  40.0,
  72.3,
  13.2,
  15.6,
  18.8,
  23.3,
  30.2,
  43.3,
  78.8,
  14.2,
  16.8,
  20.2,
  24.9,
  32.4,
  46.7,
  85.4,
  15.2,
  18.0,
  21.5,
  26.6,
  34.7,
  50.1,
  92.1,
  16.3,
  19.1,
  22.9,
  28.3,
  37.0,
  53.6,
  98.9,
  17.3,
  20.3,
  24.3,
  30.1,
  39.3,
  57.1,
  105.7,
  18.3,
  21.5,
  25.7,
  31.8,
  41.6,
  60.6,
  112.7,
  19.4,
  22.7,
  27.1,
  33.6,
  44.0,
  64.2,
  119.8,
  20.4,
  23.9,
  28.5,
  35.3,
  46.4,
  67.8,
  126.9,
  21.4,
  25.1,
  30.0,
  37.1,
  48.8,
  71.5,
  134.2,
  22.5,
  26.3,
  31.4,
  38.9,
  51.2,
  75.1,
  141.6,
  23.5,
  27.5,
  32.9,
  40.7,
  53.7,
  78.8,
  149.0,
  24.6,
  28.7,
  34.3,
  42.6,
  56.1,
  82.5,
  156.6,
  25.6,
  29.9,
  35.8,
  44.4,
  58.6,
  86.3,
  164.4,
  26.7,
  31.1,
  37.2,
  46.2,
  61.1,
  90.1,
  172.2,
  27.7,
  32.4,
  38.7,
  48.1,
  63.6,
  93.9,
  180.1,
  28.8,
  33.6,
  40.2,
  49.9,
  66.1,
  97.7,
  188.2,
  29.9,
  34.8,
  41.7,
  51.8,
  68.6,
  101.6,
  196.4,
  30.9,
  36.1,
  43.1,
  53.7,
  71.1,
  105.5,
  204.8,
  32.0,
  37.3,
  44.6,
  55.6,
  73.7,
  109.4,
  213.3,
  33.1,
  38.5,
  46.1,
  57.5,
  76.2,
  113.3,
  221.9,
  34.1,
  39.8,
  47.6,
  59.4,
  78.8,
  117.3,
  230.7,
  35.2,
  41.0,
  49.2,
  61.3,
  81.4,
  121.3,
  239.6,
  36.3,
  42.3,
  50.7,
  63.2,
  84.0,
  125.4,
  248.7,
  37.3,
  43.5,
  52.2,
  65.1,
  86.6,
  129.5,
  258.0,
  38.4,
  44.8,
  53.7,
  67.1,
  89.3,
  133.6,
  267.4,
  39.5,
  46.1,
  55.3,
  69.0,
  91.9,
  137.7,
  277.1,
  40.6,
  47.3,
  56.8,
  71.0,
  94.6,
  141.9,
  286.9,
  41.7,
  48.6,
  58.3,
  72.9,
  97.2,
  146.1,
  296.9,
  42.8,
  49.9,
  59.9,
  74.9,
  99.9,
  150.4,
  307.1,
  43.8,
  51.2,
  61.4,
  76.8,
  102.6,
  154.7,
  317.5,
  44.9,
  52.5,
  63.0,
  78.8,
  105.4,
  159.0,
  328.1,
  46.0,
  53.7,
  64.5,
  80.8,
  108.1,
  163.4,
  339.0,
  47.1,
  55.0,
  66.1,
  82.8,
  110.8,
  167.8,
  350.1,
  48.2,
  56.3,
  67.7,
  84.8,
  113.6,
  172.2,
  361.4,
  49.3,
  57.6,
  69.3,
  86.8,
  116.4,
  176.7,
  373.0,
  50.4,
  58.9,
  70.8,
  88.8,
  119.2,
  181.2,
  384.9,
  51.5,
  60.2,
  72.4,
  90.9,
  122.0,
  185.8,
  397.1,
  52.6,
  61.5,
  74.0,
  92.9,
  124.8,
  190.4,
  409.6,
  53.8,
  62.8,
  75.6,
  94.9,
  127.6,
  195.1,
  422.3,
  54.9,
  64.1,
  77.2,
  97.0,
  130.5,
  199.7,
  435.5,
  56.0,
  65.5,
  78.8,
  99.1,
  133.4,
  204.5,
  448.9,
  57.1,
  66.8,
  80.4,
  101.1,
  136.2,
  209.3,
  462.8,
  58.2,
  68.1,
  82.1,
  103.2,
  139.2,
  214.1,
  477.0,
  59.3,
  69.4,
  83.7,
  105.3,
  142.1,
  218.9,
  491.6,
  60.5,
  70.8,
  85.3,
  107.4,
  145.0,
  223.9,
  506.7,
  61.6,
  72.1,
  86.9,
  109.5,
  148.0,
  228.8,
  522.3,
  62.7,
  73.4,
  88.6,
  111.6,
  150.9,
  233.8,
  538.3,
  63.9,
  74.8,
  90.2,
  113.7,
  153.9,
  238.9,
  554.9,
  65.0,
  76.1,
  91.9,
  115.9,
  156.9,
  244.0,
  572.0,
  66.1,
  77.5,
  93.5,
  118.0,
  160.0,
  249.2,
  589.7,
  67.3,
  78.8,
  95.2,
  120.1,
  163.0,
  254.4,
  608.1,
  68.4,
  80.2,
  96.8,
  122.3,
  166.1,
  259.7,
  627.1,
  69.6,
  81.5,
  98.5,
  124.5,
  169.2,
  265.0,
  646.9,
  70.7,
  82.9,
  100.2,
  126.6,
  172.3,
  270.4,
  667.6,
  71.9,
  84.3,
  101.9,
  128.8,
  175.4,
  275.9,
  689.0,
  73.0,
  85.6,
  103.6,
  131.0,
  178.5,
  281.4,
  711.5,
  74.2,
  87.0,
  105.3,
  133.2,
  181.7,
  287.0,
  735.0,
  75.3,
  88.4,
  107.0,
  135.4,
  184.9,
  292.6,
  759.6,
  76.5,
  89.8,
  108.7,
  137.7,
  188.1,
  298.3,
  785.5,
  77.7,
  91.2,
  110.4,
  139.9,
  191.3,
  304.0,
  812.8,
  78.8,
  92.6,
  112.1,
  142.1,
  194.5,
  309.9,
  841.7,
  80.0,
  93.9,
  113.8,
  144.4,
  197.8,
  315.8,
  872.3,
  81.2,
  95.3,
  115.5,
  146.7,
  201.1,
  321.7,
  904.9,
  82.3,
  96.7,
  117.3,
  148.9,
  204.4,
  327.8,
  939.8,
  83.5,
  98.2,
  119.0,
  151.2,
  207.7,
  333.9,
  977.2,
  84.7,
  99.6,
  120.8,
  153.5,
  211.0,
  340.0,
  1017.6,
  85.9,
  101.0,
  122.5,
  155.8,
  214.4,
  346.3,
  1061.6,
  87.1,
  102.4,
  124.3,
  158.1,
  217.8,
  352.6,
  1109.7,
  88.3,
  103.8,
  126.0,
  160.5,
  221.2,
  359.1,
  1162.9,
  89.5,
  105.2,
  127.8,
  162.8,
  224.6,
  365.6,
  1222.4,
  90.7,
  106.7,
  129.6,
  165.2,
  228.1,
  372.1,
  1289.8,
  91.9,
  108.1,
  131.4,
  167.5,
  231.6,
  378.8,
  1367.7,
  93.1,
  109.5,
  133.2,
  169.9,
  235.1,
  385.6,
  1459.8,
  94.3,
  111.0,
  135.0,
  172.3,
  238.6,
  392.4,
  1572.5,
  95.5,
  112.4,
  136.8,
  174.7,
  242.2,
  399.4,
  1717.8,
  96.7,
  113.9,
  138.6,
  177.1,
  245.8,
  406.4,
  1922.6,
  97.9,
  115.3,
  140.4,
  179.5,
  249.4,
  413.6,
  2272.6,
  99.1,
  116.8,
  142.2,
  181.9,
  253.0,
  420.8,
  '>2272.6',
];

/**
 * Get MPN value for standard quantification based on positive well count
 * 
 * @param inCount - Number of positive wells (0-51)
 * @returns MPN result tuple [lower_bound, estimate, upper_bound] or undefined if invalid
 * 
 * @example
 * ```typescript
 * const result = getQtMpn(10);
 * if (result) {
 *   const [lower, estimate, upper] = result;
 *   console.log(`MPN: ${estimate} (${lower} - ${upper})`);
 * }
 * ```
 */
export const getQtMpn = (inCount: number): Mpn => {
  // Validate input range
  if (!Number.isInteger(inCount) || inCount < 0 || inCount > MAX_QT_MPN_INDEX) {
    return undefined;
  }
  
  return mpn[inCount];
};

/**
 * Get MPN value for 2000-well format using large and small position indices
 * 
 * @param inLargePos - Large position index (0-49)
 * @param inSmallPos - Small position index (0-48)
 * @returns MPN result tuple [lower_bound, estimate, upper_bound] or undefined if invalid
 * 
 * @example
 * ```typescript
 * const result = getQt2KMpn(10, 25);
 * if (result) {
 *   const [lower, estimate, upper] = result;
 *   console.log(`2K MPN: ${estimate} (${lower} - ${upper})`);
 * }
 * ```
 */
export const getQt2KMpn = (inLargePos: number, inSmallPos: number): Mpn => {
  // Validate input ranges
  if (!Number.isInteger(inLargePos) || !Number.isInteger(inSmallPos) ||
      inLargePos < 0 || inLargePos > MAX_2K_LARGE_POS ||
      inSmallPos < 0 || inSmallPos > MAX_2K_SMALL_POS) {
    return undefined;
  }
  
  // Calculate flattened array index
  const index = inLargePos * MPN_2K_MULTIPLIER + inSmallPos;
  return mpn2k[index];
};

/**
 * Get MPN value for Legionella testing using large and small position indices
 * 
 * Note: This function returns a single value from the flattened Legionella array,
 * not a tuple like the other MPN functions. The Legionella data structure is different.
 * 
 * @param inLargePos - Large position index (0-6)
 * @param inSmallPos - Small position index (0-90)
 * @returns Single MPN value (string or number) or undefined if invalid
 * 
 * @example
 * ```typescript
 * const result = getQtLegio(3, 45);
 * if (result !== undefined) {
 *   console.log(`Legionella MPN: ${result}`);
 * }
 * ```
 */
export const getQtLegio = (inLargePos: number, inSmallPos: number): string | number | undefined => {
  // Validate input ranges
  if (!Number.isInteger(inLargePos) || !Number.isInteger(inSmallPos) ||
      inLargePos < 0 || inLargePos > MAX_LEGIO_LARGE_POS ||
      inSmallPos < 0 || inSmallPos > MAX_LEGIO_SMALL_POS) {
    return undefined;
  }
  
  // Calculate flattened array index (note: different indexing than other functions)
  const index = inSmallPos * LEGIO_MULTIPLIER + inLargePos;
  return mpnLegio[index];
};
